import {
  DOC_TYPES,
  FILER_KINDS,
  NIL_SUPPLY_TYPES,
  NOTE_REASONS,
  RETURN_FORMS,
  REVERSAL_REASONS,
  REVERSE_CHARGE_KINDS,
  SUPPLY_KINDS,
  TAX_HEADS,
} from '@oneapp/gst';
import { z } from 'zod';
import { parseIndianMobile } from './account';
import type { ChatMessage, ComposerHint, IconName } from './messages';
import { isPastedTable, MAX_PASTED_BYTES } from './pasted-table';

/**
 * The chat API (Phase 2). Every route needs a signed-in person; conversations are theirs alone.
 *
 *   POST   /api/v1/conversations                 StartConversation     -> StartConversationResponse (a snapshot)
 *   GET    /api/v1/conversations?before=&limit=                        -> ConversationList
 *   GET    /api/v1/conversations/:id                                   -> ConversationSnapshot
 *   GET    /api/v1/conversations/:id/messages?before=<seq>&limit=      -> MessagePage
 *   GET    /api/v1/conversations/:id/stream       (text/event-stream; Last-Event-ID replays) -> StreamEvent per event
 *   POST   /api/v1/conversations/:id/events       ClientEvent (Idempotency-Key = client id) -> EventResponse
 *   DELETE /api/v1/conversations/:id                                   -> 204
 *   GET    /api/v1/services                                            -> ServiceCatalog
 *   POST   /api/v1/services/:id/waitlist                               -> WaitlistResponse
 */

/** The longest message someone can type. */
export const MAX_TEXT_LENGTH = 2000;

export const COMMANDS = ['menu', 'help', 'back', 'cancel', 'restart'] as const;
export type Command = (typeof COMMANDS)[number];

/**
 * A person's GST portal connection for a business: not connected; connected (the portal's session is
 * kept fresh without a new OTP until the API access period chosen on the portal ends); or reconnect
 * needed (that period ended, or the portal ended the session).
 */
export const GST_CONNECTION_STATUSES = ['not_connected', 'connected', 'reconnect_needed'] as const;
export type GstConnectionStatus = (typeof GST_CONNECTION_STATUSES)[number];

// ---------------------------------------------------------------------------
// Client → server
// ---------------------------------------------------------------------------

/**
 * What a notice's link opens in the chat (`/?open=…`): a return, as "gstr3b:<business id>:<YYYY-MM>" (gstr1,
 * gstr1a or gstr3b); or a credit note a buyer rejected in IMS (gap 4b), as "note:<id>".
 */
export const OPEN_REF =
  /^((gstr1|gstr1a|gstr3b):[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}:\d{4}-(0[1-9]|1[0-2])|note:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/;

export const ClientEventSchema = z.discriminatedUnion('type', [
  z.strictObject({
    type: z.literal('text'),
    text: z
      .string()
      .trim()
      .min(1)
      .max(MAX_PASTED_BYTES)
      .refine(
        (text) =>
          text.length <= MAX_TEXT_LENGTH ||
          (isPastedTable(text) && new TextEncoder().encode(text).length <= MAX_PASTED_BYTES),
      ),
    proposalId: z.uuid().optional(),
  }),
  z.strictObject({
    type: z.literal('choice'),
    messageId: z.string().min(1).max(64),
    choiceId: z.string().min(1).max(80),
  }),
  /** Hidden input such as an OTP: checked, never stored or echoed. */
  z.strictObject({ type: z.literal('secret'), messageId: z.string().min(1).max(64), value: z.string().min(1).max(64) }),
  z.strictObject({
    type: z.literal('widget_submit'),
    messageId: z.string().min(1).max(64),
    widgetId: z.string().min(1).max(64),
    data: z.record(z.string(), z.union([z.string().max(MAX_TEXT_LENGTH), z.number(), z.boolean()])),
  }),
  z.strictObject({
    type: z.literal('widget_action'),
    messageId: z.string().min(1).max(64),
    widgetId: z.string().min(1).max(64),
    action: z.string().min(1).max(40),
  }),
  z.strictObject({ type: z.literal('command'), command: z.enum(COMMANDS) }),
  /** A link from a notice (Phase 13): open a return in the chat, as its chip on the returns card does. */
  z.strictObject({ type: z.literal('open'), ref: z.string().regex(OPEN_REF) }),
]);
export type ClientEvent = z.infer<typeof ClientEventSchema>;

export const StartConversationSchema = z.strictObject({
  /** True for "New chat"; otherwise the latest conversation is returned (or one is started). */
  fresh: z.boolean().optional(),
});

// ---------------------------------------------------------------------------
// Server → client
// ---------------------------------------------------------------------------

/** Message content is checked by type only; channels render what they understand and skip the rest. */
const ContentSchema = z.looseObject({ type: z.string() });

export const ChatMessageSchema = z.object({
  id: z.string(),
  /** Order in the conversation. */
  seq: z.number().int(),
  /** Changes whenever the message is created or updated; SSE event ids and replay use it. */
  rev: z.number().int(),
  from: z.enum(['bot', 'user', 'system']),
  createdAt: z.string(),
  content: ContentSchema,
  status: z.enum(['active', 'answered', 'expired']).optional(),
  selected: z.string().optional(),
  composer: z.looseObject({}).optional(),
  clientRef: z.string().optional(),
}) as unknown as z.ZodType<ChatMessage>;

export const ConversationSummarySchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  createdAt: z.string(),
  lastMessageAt: z.string().nullable(),
  /** The business this chat is about (the bar under the app bar), once one is chosen. */
  business: z
    .object({ id: z.string(), name: z.string(), gstin: z.string(), connection: z.enum(GST_CONNECTION_STATUSES) })
    .nullable(),
});
export type ConversationSummary = z.infer<typeof ConversationSummarySchema>;

export const ConversationListSchema = z.object({
  conversations: z.array(ConversationSummarySchema),
  /** Pass as ?before= for the next page; null at the end. */
  nextBefore: z.string().nullable(),
});

export const ConversationSnapshotSchema = z.object({
  conversation: ConversationSummarySchema,
  /** The latest messages, oldest first. */
  messages: z.array(ChatMessageSchema),
  /** Older messages exist (GET …/messages?before=<first seq>). */
  hasMore: z.boolean(),
  /** Open the stream with Last-Event-ID: rev to get everything after this snapshot. */
  rev: z.number().int(),
  /** How the composer should behave for the question waiting for an answer. */
  composer: z.looseObject({}).nullable() as unknown as z.ZodType<ComposerHint | null>,
});
export type ConversationSnapshot = z.infer<typeof ConversationSnapshotSchema>;

/** The conversation to show, with its latest messages, so the chat opens in one round trip. */
export const StartConversationResponseSchema = ConversationSnapshotSchema.extend({
  /** A new conversation was started (the first one, or "New chat"). */
  created: z.boolean(),
});

export const MessagePageSchema = z.object({ messages: z.array(ChatMessageSchema), hasMore: z.boolean() });

/** The messages an event produced: the person's own message (if it's shown) and the replies. */
export const EventResponseSchema = z.object({ messages: z.array(ChatMessageSchema) });
export type EventResponse = z.infer<typeof EventResponseSchema>;

/** One Server-Sent Event. Events that carry a message have `id: <rev>`. */
export type StreamEvent =
  | { type: 'message'; message: ChatMessage }
  | { type: 'typing'; on: boolean }
  | { type: 'progress'; text: string }
  /** The chat's name or business changed. */
  | { type: 'conversation'; conversation: ConversationSummary }
  /** The conversation was deleted or the person signed out: stop listening. */
  | { type: 'closed'; reason: 'deleted' | 'signed_out' };

// ---------------------------------------------------------------------------
// Services (home menu, "coming soon", notify me)
// ---------------------------------------------------------------------------

export const ServiceStatusSchema = z.enum(['live', 'coming_soon']);
export type ServiceStatus = z.infer<typeof ServiceStatusSchema>;

export const ServiceSummarySchema = z.object({
  id: z.string(),
  categoryId: z.string(),
  title: z.string(),
  description: z.string(),
  icon: z.string() as unknown as z.ZodType<IconName>,
  status: ServiceStatusSchema,
});
export type ServiceSummary = z.infer<typeof ServiceSummarySchema>;

export const ServiceCategorySchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  icon: z.string() as unknown as z.ZodType<IconName>,
});

export const ServiceCatalogSchema = z.object({
  categories: z.array(ServiceCategorySchema),
  services: z.array(ServiceSummarySchema),
});
export type ServiceCatalog = z.infer<typeof ServiceCatalogSchema>;

export const WaitlistResponseSchema = z.object({ serviceId: z.string(), joinedAt: z.string() });

// ---------------------------------------------------------------------------
// GSTINs and businesses (Phase 3). Every route needs a signed-in person.
//
//   POST   /api/v1/gstin/validate          GstinValidate  -> GstinCheck (offline: format, state, check character)
//   GET    /api/v1/gstin/:gstin                           -> GstinLookup (the GST register, cached for a day)
//   GET    /api/v1/pan/:pan/gstins                        -> PanRegistrations (only when the GSP offers it)
//   POST   /api/v1/businesses              AddBusiness    -> BusinessResponse (201)
//   GET    /api/v1/businesses                             -> BusinessList
//   GET    /api/v1/businesses/:id                         -> BusinessResponse
//   PATCH  /api/v1/businesses/:id          UpdateBusiness -> BusinessResponse
//   DELETE /api/v1/businesses/:id                         -> 204
//
// Phase 14: a business's returns are seen only by the people in it: whoever connected its GST account
// (the portal's code proves it; they're owners) and whoever an owner invited. Someone who only added its
// GSTIN sees the public register details (access "added") until they connect it or are invited.
// ---------------------------------------------------------------------------

export const GstinValidateSchema = z.strictObject({ gstin: z.string().max(40) });

export const GSTIN_PROBLEMS = ['empty', 'too_short', 'too_long', 'format', 'state', 'check_digit'] as const;

export const GstinCheckSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    gstin: z.string(),
    stateCode: z.string(),
    stateName: z.string(),
    pan: z.string().nullable(),
  }),
  z.object({
    ok: z.literal(false),
    gstin: z.string(),
    problem: z.enum(GSTIN_PROBLEMS),
    /** Valid GSTINs one small slip away (a look-alike character, a swap, a wrong last character), likeliest first. */
    suggestions: z.array(z.string()),
  }),
]);
export type GstinCheckResponse = z.infer<typeof GstinCheckSchema>;

export const REGISTRATION_STATUSES = ['active', 'cancelled', 'suspended', 'inactive'] as const;
export type RegistrationStatus = (typeof REGISTRATION_STATUSES)[number];

/** A registration as the public GST register describes it. */
export const TaxpayerSchema = z.object({
  gstin: z.string(),
  /** For a proprietorship, the owner's name. */
  legalName: z.string(),
  tradeName: z.string().nullable(),
  status: z.enum(REGISTRATION_STATUSES),
  /** e.g. Proprietorship, Partnership, Private Limited Company. */
  constitution: z.string().nullable(),
  /** e.g. Regular, Composition. */
  taxpayerType: z.string().nullable(),
  /** YYYY-MM-DD. */
  registeredOn: z.string().nullable(),
  cancelledOn: z.string().nullable(),
  stateCode: z.string(),
  stateName: z.string(),
  address: z
    .object({
      line: z.string(),
      city: z.string().nullable(),
      district: z.string().nullable(),
      pincode: z.string().nullable(),
    })
    .nullable(),
  natureOfBusiness: z.array(z.string()),
  /** Monthly or quarterly (QRMP) returns, when the GSP says. */
  filingFrequency: z.enum(['monthly', 'quarterly']).nullable(),
});
export type Taxpayer = z.infer<typeof TaxpayerSchema>;

export const GstinLookupSchema = z.object({
  taxpayer: TaxpayerSchema,
  /** When the register was read; lookups are kept for a day. */
  checkedAt: z.string(),
  /** This person already added it: the business's id. */
  businessId: z.string().nullable(),
});
export type GstinLookup = z.infer<typeof GstinLookupSchema>;

export const PanRegistrationSchema = z.object({
  gstin: z.string(),
  status: z.enum(REGISTRATION_STATUSES),
  stateCode: z.string(),
  stateName: z.string(),
  /** Trade name, else legal name, when the register gives one. */
  name: z.string().nullable(),
  businessId: z.string().nullable(),
});
export const PanRegistrationsSchema = z.object({ pan: z.string(), registrations: z.array(PanRegistrationSchema) });
export type PanRegistrations = z.infer<typeof PanRegistrationsSchema>;

export const BUSINESS_ROLES = ['owner', 'staff'] as const;
export type BusinessRole = (typeof BUSINESS_ROLES)[number];

/** in: an owner or staff, who sees its returns; added: put its GSTIN in their list, but not in yet. */
export const BUSINESS_ACCESS = ['in', 'added'] as const;
export type BusinessAccess = (typeof BUSINESS_ACCESS)[number];

/** Who made a business's GST portal connection (Phase 14: there's one per business). */
export const ConnectedBySchema = z.object({ name: z.string(), you: z.boolean() });

export const NicknameSchema = z.string().trim().min(1).max(60);

export const AddBusinessSchema = z.strictObject({
  gstin: z.string().max(40),
  /**
   * What the person says they are: owner (or authorised signatory: connects the GST account next) or
   * staff (accountant: asks an owner for an invite). It only decides what comes next; being in the
   * business takes connecting its GST account or an owner's invite (Phase 14).
   */
  role: z.enum(BUSINESS_ROLES),
  nickname: NicknameSchema.optional(),
});
export type AddBusiness = z.infer<typeof AddBusinessSchema>;

export const UpdateBusinessSchema = z.strictObject({ nickname: NicknameSchema.nullable() });

export const BusinessSchema = z.object({
  id: z.string(),
  gstin: z.string(),
  /** What to call it: the nickname, else the trade name, else the legal name. */
  name: z.string(),
  legalName: z.string(),
  tradeName: z.string().nullable(),
  nickname: z.string().nullable(),
  stateCode: z.string(),
  stateName: z.string(),
  status: z.enum(REGISTRATION_STATUSES),
  taxpayerType: z.string().nullable(),
  constitution: z.string().nullable(),
  filingFrequency: z.enum(['monthly', 'quarterly']).nullable(),
  registeredOn: z.string().nullable(),
  /** owner or staff; for someone not in yet ("added"), what they said they are. */
  role: z.enum(BUSINESS_ROLES),
  /** in: sees its returns (an owner or staff); added: only its register details until connected or invited. */
  access: z.enum(BUSINESS_ACCESS),
  /** The business's GST portal connection (one per business since Phase 14); "not_connected" for anyone not in. */
  connection: z.object({
    status: z.enum(GST_CONNECTION_STATUSES),
    /** No new OTP is needed until then. */
    sessionEndsAt: z.string().nullable(),
    /** Who connected it (shown to people in the business). */
    connectedBy: ConnectedBySchema.nullable(),
  }),
  addedAt: z.string(),
  registerCheckedAt: z.string(),
});
export type Business = z.infer<typeof BusinessSchema>;

export const BusinessResponseSchema = z.object({ business: BusinessSchema });
export const BusinessListSchema = z.object({ businesses: z.array(BusinessSchema) });

// ---------------------------------------------------------------------------
// The GST portal connection (Phase 4). Since Phase 14 there's one per business: an owner (or someone
// proving the business is theirs) connects it with the portal username and an OTP, and everyone in the
// business works through it. Staff never connect: an owner does it again when it ends.
//
//   GET    /api/v1/businesses/:id/gst-connection                           -> GstConnectionResponse
//   POST   /api/v1/businesses/:id/gst-connection/otp      RequestGstOtp    -> GstConnectionResponse
//   POST   /api/v1/businesses/:id/gst-connection/verify   VerifyGstOtp     -> GstConnectionResponse
//   POST   /api/v1/businesses/:id/gst-connection/refresh                   -> GstConnectionResponse
//   DELETE /api/v1/businesses/:id/gst-connection                           -> 204
// ---------------------------------------------------------------------------

/** GST portal OTPs are 6 digits. */
export const GST_OTP_LENGTH = 6;

/**
 * A GST portal username as people type it: no spaces; letters, digits, dot, underscore or hyphen. The
 * portal applies its own rules; this only catches what can't be a username.
 */
export const GstUsernameSchema = z
  .string()
  .trim()
  .min(3, { error: 'Enter your GST portal username' })
  .max(40, { error: 'That’s longer than a GST portal username' })
  .regex(/^[A-Za-z0-9._-]+$/, { error: 'Use letters, digits and . _ - only, with no spaces' });

export const RequestGstOtpSchema = z.strictObject({
  username: GstUsernameSchema,
  /** The version of the consent text the person agreed to (DATA_CONSENT_VERSIONS.gst_data). */
  consentVersion: z.string().min(1).max(40),
});
export type RequestGstOtp = z.input<typeof RequestGstOtpSchema>;

export const VerifyGstOtpSchema = z.strictObject({
  otp: z
    .string()
    .trim()
    .regex(/^\d{6}$/, { error: 'The code has 6 digits' }),
});
export type VerifyGstOtp = z.input<typeof VerifyGstOtpSchema>;

/** The OTP waiting to be entered. */
export const GstOtpSchema = z.object({
  sentAt: z.string(),
  expiresAt: z.string(),
  /** Another code can be sent from then. */
  resendAt: z.string(),
  attemptsLeft: z.number().int().min(0),
  /** Where the portal sent it, masked, when the GSP says (e.g. "+91 ••••• ••321"). */
  sentTo: z.array(z.string()),
});
export type GstOtp = z.infer<typeof GstOtpSchema>;

export const GstConnectionSchema = z.object({
  businessId: z.string(),
  status: z.enum(GST_CONNECTION_STATUSES),
  /** Who made it; null when it isn't connected (or the person isn't in the business yet). */
  connectedBy: ConnectedBySchema.nullable(),
  /** The portal username it uses (or a waiting code was sent for); shown to owners only. */
  username: z.string().nullable(),
  connectedAt: z.string().nullable(),
  /** No new OTP is needed until then: the end of the API access period chosen on the portal (null if unknown). */
  sessionEndsAt: z.string().nullable(),
  otp: GstOtpSchema.nullable(),
  /** After too many wrong codes, connecting is paused until then. */
  lockedUntil: z.string().nullable(),
  /** The fixed OTP of the twin and the GSP sandbox (575757), to show while testing; null for real portals. */
  testOtp: z.string().nullable(),
});
export type GstConnection = z.infer<typeof GstConnectionSchema>;

export const GstConnectionResponseSchema = z.object({ connection: GstConnectionSchema });

// ---------------------------------------------------------------------------
// Returns, dues and filings (Phase 5). Statuses come from the GST portal's return tracking, which is
// public, so they're the same for everyone who added a business; due dates and late fees are worked
// out by EkApp (packages/gst). Every route needs a signed-in person, and only shows their businesses.
//
//   GET /api/v1/businesses/:id/returns?fy=2026-27            -> BusinessReturns
//   GET /api/v1/dues                                         -> Dues
//   GET /api/v1/filings?businessId=&form=&cursor=&limit=     -> FilingList
//   GET /api/v1/filings/:id                                  -> FilingResponse
// ---------------------------------------------------------------------------

export type { FilerKind, ReturnForm, SupplyKind } from '@oneapp/gst';
export { FILER_KINDS, RETURN_FORMS, SUPPLY_KINDS };

/** The forms a filing can be: the returns, and (gap 4b) GSTR-1A, a filed month's fixes (it has no due date). */
export const FILING_FORMS = [...RETURN_FORMS, 'gstr1a'] as const;
export type FilingForm = (typeof FILING_FORMS)[number];

/** filed; late (past its due date, not filed); upcoming (not due yet, or due today); time_barred (can't be filed any more). */
export const RETURN_STATES = ['filed', 'late', 'upcoming', 'time_barred'] as const;
export type ReturnState = (typeof RETURN_STATES)[number];

export const PERIOD_KINDS = ['month', 'quarter', 'year'] as const;

export const ReturnStatusSchema = z.object({
  form: z.enum(RETURN_FORMS),
  /** The period's last month, YYYY-MM: the month, a quarter's last month, or March for a year. */
  period: z.string(),
  periodKind: z.enum(PERIOD_KINDS),
  /** YYYY-MM-DD. */
  dueOn: z.string(),
  /** The government extended it: the usual date and why (null when it's the usual date). */
  extension: z
    .object({ normalDueOn: z.string(), reason: z.string().nullable(), reference: z.string().nullable() })
    .nullable(),
  state: z.enum(RETURN_STATES),
  filedOn: z.string().nullable(),
  /** Days after the due date it was filed (0: on time). */
  filedDaysLate: z.number().int().nullable(),
  arn: z.string().nullable(),
  /** For GET /filings/:id. */
  filingId: z.string().nullable(),
  /** Days past the due date so far, while it's late. */
  daysLate: z.number().int().nullable(),
  /** Days until the due date (0: today), while it's upcoming. */
  daysLeft: z.number().int().nullable(),
  /** Late fee so far, in paise: an estimate until it's filed (₹50 a day, capped by turnover). */
  lateFee: z.number().int(),
});
export type ReturnStatus = z.infer<typeof ReturnStatusSchema>;

export const BusinessReturnsSchema = z.object({
  businessId: z.string(),
  /** The financial year shown, e.g. 2026-27. */
  fy: z.string(),
  /** How the registration files; null when EkApp doesn't track its returns (an ISD, a TDS deductor…). */
  filer: z.enum(FILER_KINDS).nullable(),
  /** The year's returns so far (and the one in progress), oldest first. */
  returns: z.array(ReturnStatusSchema),
  /** Late returns from earlier years that can still be filed. */
  earlierLate: z.array(ReturnStatusSchema),
  /** Late fees so far on every late return above, in paise. */
  lateFeeSoFar: z.number().int(),
  /** The soonest return not due yet (possibly in the next year), for "next up". */
  next: ReturnStatusSchema.nullable(),
  /** When the GST portal was last read for this (null: never). */
  asOf: z.string().nullable(),
  /** The portal couldn't be read just now: this is the last known state, as of `asOf`. */
  stale: z.boolean(),
});
export type BusinessReturns = z.infer<typeof BusinessReturnsSchema>;

export const DueItemSchema = ReturnStatusSchema.extend({
  businessId: z.string(),
  businessName: z.string(),
  gstin: z.string(),
});
export type DueItem = z.infer<typeof DueItemSchema>;

export const DuesSchema = z.object({
  /** Late returns and any due within a week: by business (in the person's order), most overdue first. */
  due: z.array(DueItemSchema),
  /** Late fees so far on the late ones, in paise. */
  lateFeeSoFar: z.number().int(),
  /** The next return due after these, across the businesses. */
  next: DueItemSchema.nullable(),
  /** Each business: whether its returns could be read, and as of when. */
  businesses: z.array(
    z.object({
      businessId: z.string(),
      name: z.string(),
      gstin: z.string(),
      filer: z.enum(FILER_KINDS).nullable(),
      asOf: z.string().nullable(),
      stale: z.boolean(),
      /** False when the portal has never been read for it (it's down, or GSP access isn't set up). */
      available: z.boolean(),
    }),
  ),
});
export type Dues = z.infer<typeof DuesSchema>;

export const FILING_SOURCES = ['portal', 'oneapp'] as const;

export const FilingSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  businessName: z.string(),
  gstin: z.string(),
  form: z.enum(FILING_FORMS),
  period: z.string(),
  periodKind: z.enum(PERIOD_KINDS),
  fy: z.string(),
  filedOn: z.string(),
  arn: z.string().nullable(),
  /** How it was filed, as the portal says: ONLINE, OFFLINE, SMS. */
  mode: z.string().nullable(),
  /** portal: filed on the GST portal (or elsewhere) and read from it; oneapp: filed through EkApp. */
  source: z.enum(FILING_SOURCES),
  /** Its due date, and how many days after it it was filed (0: on time). */
  dueOn: z.string().nullable(),
  filedDaysLate: z.number().int().nullable(),
  /** Filed through EkApp: who signed it and how (EVC), and when exactly. Null for one read from the portal. */
  signedBy: z.object({ name: z.string(), method: z.enum(['evc']) }).nullable(),
  /** Prepared by staff and approved by the owner who signed it (Phase 14): the preparer's name. */
  preparedBy: z.string().nullable(),
  filedAt: z.string().nullable(),
  /** A GSTR-3B filed through EkApp: tax paid in cash and from credit, and the challan paid for it (Phase 13). */
  paid: z
    .object({
      cash: z.number().int(),
      credit: z.number().int(),
      challan: z
        .object({
          id: z.string(),
          cpin: z.string(),
          cin: z.string().nullable(),
          bankName: z.string(),
          paidOn: z.string().nullable(),
          amount: z.number().int(),
        })
        .nullable(),
    })
    .nullable(),
  /** A GSTR-1 filed through EkApp: how many sales, their taxable value and tax (Phase 13). */
  sales: z.object({ invoices: z.number().int(), taxable: z.number().int(), tax: z.number().int() }).nullable(),
  /** Filed NIL through EkApp (Phase 12): nothing to report for the month. */
  nil: z.boolean(),
});
export type Filing = z.infer<typeof FilingSchema>;

export const FilingListSchema = z.object({
  /** Newest period first. */
  filings: z.array(FilingSchema),
  /** Pass as ?cursor= for the next page; null at the end. */
  nextCursor: z.string().nullable(),
});
export type FilingList = z.infer<typeof FilingListSchema>;

export const FilingResponseSchema = z.object({ filing: FilingSchema });

// ---------------------------------------------------------------------------
// GSTR-1 sales (Phase 6): a draft per business, form and period, its invoices, what needs a look, and
// the tables they make. Every route needs a signed-in person who added the business.
//
//   POST   /api/v1/businesses/:id/returns                   OpenReturn        -> ReturnDraftResponse (201 new, 200 open)
//   GET    /api/v1/returns/:rid                                                -> ReturnDraftResponse
//   GET    /api/v1/returns/:rid/invoices?kind=b2b|b2c&cursor=                  -> InvoiceList
//   POST   /api/v1/returns/:rid/invoices                    SaleInput         -> InvoiceResponse (201)
//   PUT    /api/v1/returns/:rid/invoices/:iid  (If-Match: version) SaleInput -> InvoiceResponse
//   DELETE /api/v1/returns/:rid/invoices/:iid  (If-Match: version)            -> ReturnDraftResponse
//   GET    /api/v1/returns/:rid/summary                                        -> Gstr1SummaryResponse
//   GET    /api/v1/returns/:rid/gst-free                                       -> GstFreeResponse (gap 4)
//   PUT    /api/v1/returns/:rid/gst-free                    PutGstFree        -> GstFreeResponse
//   GET    /api/v1/businesses/:id/invoices?q=                                  -> OriginalList (a note's invoices)
//
// Credit and debit notes (gap 4) are sales too: docType credit or debit, the invoices they're for
// (`against`) and why (`reason`); their amounts are positive and their type gives the sign.
//   POST   /api/v1/returns/:rid/issues/:issueId/resolve     ResolveIssue      -> ReturnDraftResponse
//   GET    /api/v1/hsn?q=&date=                                                -> HsnSearch
//   POST   /api/v1/assist/parse-sale                        ParseSale         -> ParsedSale
// ---------------------------------------------------------------------------

const isoDateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { error: 'Use YYYY-MM-DD' });

export const OpenReturnSchema = z.strictObject({
  /** gstr1a (gap 4b): a filed month's GSTR-1A, its fixes, from GSTR-1's due date until GSTR-3B is filed. */
  form: z.enum(['gstr1', 'gstr1a', 'gstr3b']),
  /** YYYY-MM */
  period: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, { error: 'Use YYYY-MM' }),
});
export type OpenReturn = z.infer<typeof OpenReturnSchema>;

/** Amounts in paise. */
export const AmountsSchema = z.object({
  taxableValue: z.number().int(),
  igst: z.number().int(),
  cgst: z.number().int(),
  sgst: z.number().int(),
  cess: z.number().int(),
});

export const SaleLineInputSchema = z.strictObject({
  /** HSN (goods) or SAC (services): 4, 6 or 8 digits. */
  hsn: z
    .string()
    .trim()
    .regex(/^\d{4}$|^\d{6}$|^\d{8}$/, { error: 'Use a 4, 6 or 8-digit HSN or SAC code' }),
  description: z.string().trim().max(120).optional(),
  /** Unit quantity code (NOS, KGS…); NA for services. Defaults from the HSN code. */
  uqc: z.string().trim().toUpperCase().max(3).optional(),
  quantity: z.number().min(0).max(1e9).optional(),
  /** Percent, e.g. 18 or 0.25. */
  rate: z.number().min(0).max(100),
  /** Paise, before GST. */
  taxableValue: z.number().int().min(0).max(1e13),
});
export type SaleLineInput = z.input<typeof SaleLineInputSchema>;

/** An invoice a credit or debit note is for: EkApp's own (invoiceId), or one from before EkApp. */
export const NoteAgainstSchema = z.strictObject({
  invoiceId: z.string().uuid().nullable().optional(),
  number: z.string().trim().min(1).max(40),
  date: isoDateString,
});
export type NoteAgainst = z.infer<typeof NoteAgainstSchema>;

export const SaleInputSchema = z.strictObject({
  /** invoice (the default), or a credit or debit note (gap 4). */
  docType: z.enum(DOC_TYPES).optional(),
  /** A note's invoices, all of one financial year. */
  against: z.array(NoteAgainstSchema).min(1).max(20).optional(),
  /** Why the note was made. */
  reason: z.enum(NOTE_REASONS).optional(),
  /**
   * A note on an invoice from before EkApp, to a consumer in another state: that invoice's value in
   * paise (a large B2C invoice's note is listed on its own).
   */
  originalValue: z.number().int().min(0).max(1e13).optional(),
  /** One invoice number, or several consumer bills entered together. For a note, its number. */
  numbers: z.array(z.string().trim().min(1).max(40)).min(1).max(200),
  date: isoDateString,
  /** The registered buyer's GSTIN; null for a consumer (unregistered). */
  buyerGstin: z.string().trim().max(40).nullable(),
  /** State code where the goods went, when not the buyer's state (or, for a consumer, not the seller's). */
  placeOfSupply: z
    .string()
    .regex(/^\d{2}$/)
    .nullable()
    .optional(),
  lines: z.array(SaleLineInputSchema).min(1).max(50),
});
export type SaleInput = z.input<typeof SaleInputSchema>;

export const SaleLineSchema = AmountsSchema.extend({
  hsn: z.string(),
  description: z.string().nullable(),
  uqc: z.string(),
  quantity: z.number(),
  rate: z.number(),
});
export type SaleLine = z.infer<typeof SaleLineSchema>;

export const InvoiceSchema = AmountsSchema.extend({
  id: z.string(),
  returnId: z.string(),
  /** invoice, or a credit or debit note (gap 4). */
  docType: z.enum(DOC_TYPES),
  /** A note's invoices; empty for an invoice. */
  against: z.array(z.object({ invoiceId: z.string().nullable(), number: z.string(), date: z.string() })),
  reason: z.enum(NOTE_REASONS).nullable(),
  /** In a GSTR-1A (gap 4b): the filed sale this corrects (under the same number); null for one added. */
  amends: z.string().nullable(),
  numbers: z.array(z.string()),
  date: z.string(),
  /** Where it goes; for a note, where its invoice went. */
  kind: z.enum(SUPPLY_KINDS),
  buyer: z
    .object({
      gstin: z.string(),
      name: z.string().nullable(),
      stateCode: z.string(),
    })
    .nullable(),
  placeOfSupply: z.string(),
  interState: z.boolean(),
  lines: z.array(SaleLineSchema),
  tax: z.number().int(),
  invoiceValue: z.number().int(),
  /** page, chat, import, or walk_in (Phase 16: walk-in totals by rate, with their bill numbers). */
  source: z.string(),
  /** Walk-in sales: the bills cancelled (counted in Table 13 only). Empty for any other sale. */
  cancelled: z.array(z.string()),
  /** What it was moved from, e.g. b2b for a cancelled buyer's sale now in B2C. */
  movedFrom: z.enum(SUPPLY_KINDS).nullable(),
  /** Changed since it was added. */
  edited: z.boolean(),
  version: z.number().int(),
  updatedAt: z.string(),
});
export type Invoice = z.infer<typeof InvoiceSchema>;

export const ISSUE_KINDS = ['buyer_cancelled', 'buyer_suspended', 'buyer_not_found', 'date_outside_period'] as const;
export const ISSUE_ACTIONS = ['move_to_b2c', 'remove', 'keep'] as const;

export const ReturnIssueSchema = z.object({
  id: z.string(),
  kind: z.enum(ISSUE_KINDS),
  invoiceId: z.string().nullable(),
  invoiceNumber: z.string().nullable(),
  /** What's wrong, for people. */
  detail: z.string(),
  /** What can fix it here (editing the invoice always can). */
  actions: z.array(z.enum(ISSUE_ACTIONS)),
});
export type ReturnIssue = z.infer<typeof ReturnIssueSchema>;

export const ReturnDraftSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  /** gstr1a (gap 4b): a filed month's fixes, its sales the ones added and the ones corrected. */
  form: z.enum(['gstr1', 'gstr1a']),
  period: z.string(),
  /** draft; filing: confirmed and on its way to the GST portal (sales can't change); filed. */
  status: z.enum(['draft', 'filing', 'filed']),
  /** Goes up with every change, for If-Match and to tell a screen it's out of date. */
  version: z.number().int(),
  /** Sales (all, B2B, B2C) and credit or debit notes. */
  counts: z.object({
    all: z.number().int(),
    b2b: z.number().int(),
    b2c: z.number().int(),
    notes: z.number().int(),
  }),
  totals: AmountsSchema.extend({ tax: z.number().int(), invoiceValue: z.number().int() }),
  issues: z.array(ReturnIssueSchema),
  updatedAt: z.string(),
});
export type ReturnDraft = z.infer<typeof ReturnDraftSchema>;

export const ReturnDraftResponseSchema = z.object({ return: ReturnDraftSchema });
export const InvoiceResponseSchema = z.object({ invoice: InvoiceSchema, return: ReturnDraftSchema });
export const InvoiceListSchema = z.object({ invoices: z.array(InvoiceSchema), nextCursor: z.string().nullable() });
export type InvoiceList = z.infer<typeof InvoiceListSchema>;

export const ResolveIssueSchema = z.strictObject({ action: z.enum(ISSUE_ACTIONS) });

const KindTotalsSchema = AmountsSchema.extend({ count: z.number().int(), invoiceValue: z.number().int() });
const NoteTotalsSchema = AmountsSchema.extend({ count: z.number().int(), value: z.number().int() });
const NilAmountsSchema = z.object({ nil: z.number().int(), exempt: z.number().int(), nonGst: z.number().int() });
export const NilRowSchema = NilAmountsSchema.extend({ supplyType: z.enum(NIL_SUPPLY_TYPES) });
export type NilRowView = z.infer<typeof NilRowSchema>;
const DocumentSeriesSchema = z.object({
  from: z.string(),
  to: z.string(),
  total: z.number().int(),
  cancelled: z.number().int(),
  net: z.number().int(),
});
export const HsnSummaryRowSchema = AmountsSchema.extend({
  hsn: z.string(),
  description: z.string().nullable(),
  uqc: z.string(),
  quantity: z.number(),
  rate: z.number(),
  totalValue: z.number().int(),
});

export const Gstr1SummarySchema = z.object({
  /** Table 4A */
  b2b: KindTotalsSchema,
  /** Table 5 */
  b2cl: KindTotalsSchema,
  /** Table 7, with its rows by place of supply and rate, net of the notes on consumer bills (credit, debit). */
  b2cs: KindTotalsSchema.extend({
    rows: z.array(AmountsSchema.extend({ placeOfSupply: z.string(), rate: z.number(), interState: z.boolean() })),
    credit: NoteTotalsSchema,
    debit: NoteTotalsSchema,
  }),
  /** Table 9B: notes on B2B invoices (registered) and large B2C invoices (unregistered). */
  notes: z.object({
    credit: NoteTotalsSchema,
    debit: NoteTotalsSchema,
    registered: z.number().int(),
    unregistered: z.number().int(),
  }),
  /** Table 8: GST-free sales; fromBills is the part from 0% lines. */
  nil: z.object({ rows: z.array(NilRowSchema), total: NilAmountsSchema, fromBills: z.number().int() }),
  /** Table 12, B2B and B2C apart. */
  hsn: z.object({ b2b: z.array(HsnSummaryRowSchema), b2c: z.array(HsnSummaryRowSchema) }),
  /** Table 13: invoices, credit notes and debit notes. */
  documents: z.array(DocumentSeriesSchema),
  creditNotes: z.array(DocumentSeriesSchema),
  debitNotes: z.array(DocumentSeriesSchema),
  totals: KindTotalsSchema.extend({ tax: z.number().int() }),
});
export type Gstr1SummaryView = z.infer<typeof Gstr1SummarySchema>;
export const Gstr1SummaryResponseSchema = z.object({ summary: Gstr1SummarySchema, return: ReturnDraftSchema });
export type Gstr1SummaryResponse = z.infer<typeof Gstr1SummaryResponseSchema>;

/**
 * GST-free sales for a month (Table 8, gap 4): what the person gave (`rows`), what came from 0% bills
 * (`fromBills`), whether they've said for this month (`given`), and whether they said the business ever
 * sells GST-free things (asked once; null until then).
 */
export const GstFreeSchema = z.object({
  given: z.boolean(),
  rows: z.array(NilRowSchema),
  fromBills: z.array(NilRowSchema),
  sellsGstFree: z.boolean().nullable(),
});
export type GstFree = z.infer<typeof GstFreeSchema>;
export const GstFreeResponseSchema = z.object({ gstFree: GstFreeSchema, return: ReturnDraftSchema });
export type GstFreeResponse = z.infer<typeof GstFreeResponseSchema>;

const gstFreePaise = z.number().int().min(0).max(1e13);
export const PutGstFreeSchema = z.strictObject({
  /** Every row the person gave; none (or zeros): nothing GST-free this month. */
  rows: z
    .array(
      z.strictObject({
        supplyType: z.enum(NIL_SUPPLY_TYPES),
        nil: gstFreePaise,
        exempt: gstFreePaise,
        nonGst: gstFreePaise,
      }),
    )
    .max(NIL_SUPPLY_TYPES.length),
  /** Their answer to "does the business sell GST-free things?", when they give it here. */
  sellsGstFree: z.boolean().optional(),
});
export type PutGstFree = z.input<typeof PutGstFreeSchema>;

/** An invoice a note can be for (gap 4): this financial year's or last year's, still open for a credit note. */
export const OriginalInvoiceSchema = z.object({
  id: z.string(),
  returnId: z.string(),
  period: z.string(),
  number: z.string(),
  /** The bill numbers, for walk-in sales entered together. */
  numbers: z.array(z.string()),
  date: z.string(),
  kind: z.enum(SUPPLY_KINDS),
  buyer: z.object({ gstin: z.string(), name: z.string().nullable() }).nullable(),
  placeOfSupply: z.string(),
  interState: z.boolean(),
  invoiceValue: z.number().int(),
  taxableValue: z.number().int(),
  lines: z.array(SaleLineSchema),
  /** Taxable value already taken back by credit notes. */
  credited: z.number().int(),
  /** The last day a credit note on it can be reported. */
  creditNoteDeadline: z.string(),
  walkIn: z.boolean(),
});
export type OriginalInvoice = z.infer<typeof OriginalInvoiceSchema>;
export const OriginalListSchema = z.object({ invoices: z.array(OriginalInvoiceSchema) });
export type OriginalList = z.infer<typeof OriginalListSchema>;
/** The next number in a business's credit or debit note series. */
export const NextNoteNumberSchema = z.object({ number: z.string() });

/** Where GSTR-1A stands for a month (gap 4b): open, not open yet (until opensOn), or why it's closed. */
export const GSTR1A_WINDOW_STATES = ['open', 'not_yet', 'gstr1_not_filed', 'gstr3b_filed', 'filed'] as const;
export const Gstr1aWindowSchema = z.object({
  state: z.enum(GSTR1A_WINDOW_STATES),
  opensOn: z.string().nullable(),
});
export type Gstr1aWindowView = z.infer<typeof Gstr1aWindowSchema>;

const TaxAmountsSchema = AmountsSchema.extend({ tax: z.number().int() });

/** One fix in a GSTR-1A: a sale or note added, or a filed sale corrected (its record as filed, and now). */
export const Gstr1aChangeSchema = z.object({
  kind: z.enum(['added', 'corrected']),
  invoice: InvoiceSchema,
  before: InvoiceSchema.nullable(),
});
export type Gstr1aChange = z.infer<typeof Gstr1aChangeSchema>;

/**
 * A month's GSTR-1A (gap 4b): the fixes, and what they do to the month's GSTR-3B (tax on sales, Table 3.1(a):
 * from GSTR-1, and with GSTR-1A). `gstr1ReturnId` is the month's GSTR-1 filed through EkApp, whose sales can be
 * corrected; null when it was filed elsewhere (sales and notes can only be added then).
 */
export const Gstr1aViewSchema = z.object({
  return: ReturnDraftSchema,
  gstr1ReturnId: z.string().nullable(),
  window: Gstr1aWindowSchema,
  changes: z.array(Gstr1aChangeSchema),
  effect: z.object({ before: TaxAmountsSchema, after: TaxAmountsSchema, change: TaxAmountsSchema }),
});
export type Gstr1aView = z.infer<typeof Gstr1aViewSchema>;

/**
 * A credit note a buyer rejected in IMS (gap 4b, K10): once their GSTR-3B is filed they keep the credit, and the
 * note's tax comes back in the seller's GSTR-3B for `period` (Table 3.1(a), filled by the portal). `invoiceId` is
 * the note as made in EkApp, with what it was against; null for a note from before EkApp.
 */
export const NoteRejectionSchema = AmountsSchema.extend({
  id: z.string(),
  businessId: z.string(),
  buyer: z.object({ gstin: z.string(), name: z.string().nullable() }),
  number: z.string(),
  date: z.string().nullable(),
  period: z.string(),
  value: z.number().int(),
  tax: z.number().int(),
  invoiceId: z.string().nullable(),
  against: z.array(z.object({ number: z.string(), date: z.string() })),
  seenAt: z.string(),
});
export type NoteRejection = z.infer<typeof NoteRejectionSchema>;

export const HsnResultSchema = z.object({
  /** Start of the rate entry, when known. */
  validFrom: z.string().optional(),
  code: z.string(),
  description: z.string(),
  kind: z.enum(['goods', 'services']),
  /** Percent, valid on the date asked about; null where it depends on more than the code. */
  rate: z.number().nullable(),
  /** Where the rate depends on the price per unit: above `priceLimit` paise, `rateAbove` applies. */
  priceRule: z.object({ priceLimit: z.number().int(), rateAbove: z.number() }).nullable(),
  uqc: z.string(),
});
export type HsnResult = z.infer<typeof HsnResultSchema>;

/** The GST rates in use on a date: the common ones first, then every one for goods and for services. */
export const RatesSchema = z.object({
  common: z.array(z.number()),
  goods: z.array(z.number()),
  services: z.array(z.number()),
});
export const HsnSearchSchema = z.object({ results: z.array(HsnResultSchema), rates: RatesSchema });
export type HsnSearch = z.infer<typeof HsnSearchSchema>;

/** A buyer this business sold to before (from the GST register when the sale was added). */
export const SavedBuyerSchema = z.object({
  gstin: z.string(),
  name: z.string(),
  legalName: z.string().nullable(),
  stateCode: z.string(),
  stateName: z.string(),
  lastUsedAt: z.string(),
  uses: z.number().int(),
});
export type SavedBuyer = z.infer<typeof SavedBuyerSchema>;
export const SavedBuyerListSchema = z.object({ buyers: z.array(SavedBuyerSchema) });

export const ParseSaleSchema = z.strictObject({
  text: z.string().trim().min(1).max(1000),
  /** The return it's for: its business's saved buyers and invoice series help. */
  returnId: z.string().max(64).optional(),
});

const found = <T extends z.ZodType>(value: T) =>
  z.object({ value, confidence: z.number().min(0).max(1), by: z.enum(['rules', 'ai', 'buyers', 'hsn']) }).nullable();

export const ParsedSaleSchema = z.object({
  fields: z.object({
    numbers: found(z.array(z.string())),
    /** How many bills the message is about ("3 bills, all walk-in"), when it says. */
    bills: found(z.number().int()),
    date: found(z.string()),
    buyerGstin: found(z.string()),
    buyerName: found(z.string()),
    /** Delivery state read from the typed sale. */
    delivery: found(z.string()).optional(),
    consumer: found(z.boolean()),
    item: found(z.string()),
    quantity: found(z.number()),
    hsn: found(z.string()),
    rate: found(z.number()),
    /** Paise. */
    amount: found(z.number().int()),
    /** Whether the amount includes GST; null when the message doesn't say. */
    includesGst: found(z.boolean()),
  }),
  /** What's still needed before the sale can be read back. */
  missing: z.array(z.string()),
});
export type ParsedSale = z.infer<typeof ParsedSaleSchema>;

// ---------------------------------------------------------------------------
// Uploads and imports (Phase 7): a sales register (CSV or Excel) goes straight to storage through a
// short-lived link, is scanned for malware, then read and checked row by row. Rows that need it get a
// problem with a suggested fix; the ready ones are added to the return. Imports are the uploader's own.
// Phase 8 adds photos of invoices (and PDF pages, sent as pictures): read by the AI, with the fields it
// wasn't sure of asked about ("checks") before the same row checks run.
//
//   POST /api/v1/uploads                               CreateUpload     -> UploadTicket (201)
//   POST /api/v1/uploads/:id/complete                                   -> UploadResponse
//   GET  /api/v1/uploads/:id/view                                       -> UploadView (a photo, briefly)
//   POST /api/v1/returns/:rid/imports                  StartImport      -> ImportResponse (202)
//   GET  /api/v1/imports/:id                                            -> ImportResponse
//   GET  /api/v1/imports/:id/checks                                     -> ImportChecksResponse
//   POST /api/v1/imports/:id/checks/:cid/confirm       ConfirmCheck     -> ImportResponse
//   GET  /api/v1/imports/:id/problems?cursor=&limit=                    -> ImportProblemList
//   GET  /api/v1/imports/:id/rows/:rowId                                -> ImportRowResponse
//   POST /api/v1/imports/:id/problems/:pid/resolve     ResolveProblem   -> ImportResponse
//   POST /api/v1/imports/:id/apply                                      -> ApplyImportResponse
//   GET  /api/v1/templates/sales-register?format=csv|xlsx               -> the template file
//
// Phase 16 reads a bank or UPI statement the same way (kind bank_statement): see "No records" below.
// ---------------------------------------------------------------------------

export const UPLOAD_PURPOSES = ['sales_file', 'invoice_photo', 'bank_statement', 'gst_notice'] as const;
export type UploadPurpose = (typeof UPLOAD_PURPOSES)[number];

/**
 * A photo of an invoice, as the app sends it: shrunk and saved as a JPEG on the phone (HEIC photos and
 * PDF pages too), so every photo reaches the server as one of these.
 */
export const INVOICE_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
/** Photos (or PDF pages) read in one go; pages of one PDF; and pages a person can have read a month. */
export const MAX_PHOTO_PAGES = 20;
export const MAX_PDF_PAGES = 10;
export const PHOTO_PAGES_PER_MONTH = 300;

/** The largest file that can be uploaded: 5 MB. */
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

/** What a sales file may be: its name's ending and the types browsers report for it. */
export const SALES_FILE_TYPES = {
  csv: ['text/csv', 'application/csv', 'text/comma-separated-values', 'application/vnd.ms-excel', 'text/plain'],
  xlsx: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
} as const;

/** What a bank or UPI statement may be (Phase 16): a PDF, or the bank's Excel or CSV download. */
export const STATEMENT_FILE_TYPES = {
  pdf: ['application/pdf', 'application/x-pdf'],
  csv: SALES_FILE_TYPES.csv,
  xlsx: SALES_FILE_TYPES.xlsx,
} as const;

export const UPLOAD_STATUSES = ['pending', 'scanning', 'clean', 'rejected', 'deleted'] as const;
/** Why an upload was refused: not there, not the size it said, not a CSV or .xlsx inside, or unsafe. */
export const UPLOAD_REJECTIONS = ['missing', 'size', 'wrong_type', 'old_excel', 'infected', 'scan_failed'] as const;

export const CreateUploadSchema = z.strictObject({
  purpose: z.enum(UPLOAD_PURPOSES),
  fileName: z.string().trim().min(1).max(200),
  contentType: z.string().trim().max(120),
  /** Bytes. The upload link takes exactly this many. */
  size: z.number().int().min(1),
});
export type CreateUpload = z.infer<typeof CreateUploadSchema>;

export const UploadSchema = z.object({
  id: z.string(),
  purpose: z.enum(UPLOAD_PURPOSES),
  fileName: z.string(),
  contentType: z.string(),
  size: z.number().int(),
  /** pending: the link is made; clean: it's in and passed the checks; rejected: see `rejectReason`. */
  status: z.enum(UPLOAD_STATUSES),
  rejectReason: z.enum(UPLOAD_REJECTIONS).nullable(),
  /** What the file really is, from its bytes. */
  detectedType: z.string().nullable(),
  createdAt: z.string(),
});
export type Upload = z.infer<typeof UploadSchema>;

export const UploadTicketSchema = z.object({
  upload: UploadSchema,
  /** PUT the file's bytes here, with exactly these headers, before `expiresAt` (5 minutes). */
  url: z.string(),
  method: z.literal('PUT'),
  headers: z.record(z.string(), z.string()),
  expiresAt: z.string(),
});
export type UploadTicket = z.infer<typeof UploadTicketSchema>;
export const UploadResponseSchema = z.object({ upload: UploadSchema });

export const IMPORT_KINDS = ['sales_file', 'invoice_photos', 'bank_statement'] as const;
export type ImportKind = (typeof IMPORT_KINDS)[number];
/**
 * queued → reading → (photos: confirming, while the person checks what the AI wasn't sure of) →
 * checking → ready | failed. A statement with a password waits in locked until it's typed.
 */
export const IMPORT_STATUSES = ['queued', 'reading', 'locked', 'confirming', 'checking', 'ready', 'failed'] as const;
export type ImportStatus = (typeof IMPORT_STATUSES)[number];

/** Why a whole file couldn't be read, so the person knows what to change. */
export const IMPORT_FAILURES = [
  'empty',
  'wrong_type',
  'old_excel',
  'html_excel',
  'macros',
  'unreadable',
  'too_big',
  'no_table',
  'missing_columns',
  'no_rows',
  'too_many_rows',
  'infected',
  'scan_failed',
  'file_gone',
  /** Photos: none of them is a sales invoice of this business (all left out, each with why). */
  'no_invoices',
  /** Photos: the AI couldn't be reached, or kept failing. */
  'reader_failed',
  /** Statements: the password was wrong too many times (the file is deleted). */
  'password_tries',
  /** Statements: a PDF with no text in it (a scan or a photo). */
  'scanned',
  /** Statements: none of its payments is in the return's month. */
  'other_period',
] as const;
export type ImportFailure = (typeof IMPORT_FAILURES)[number];

/** Why a photo (or a PDF page) wasn't read as a sale. */
export const PHOTO_LEFT_OUT = [
  /** A quotation, an estimate, a delivery challan… */
  'not_invoice',
  /** Another business's invoice: a purchase, not this business's sale. */
  'not_ours',
  'credit_note',
  /** Too dark, blurred or cut off to read. */
  'unreadable',
  /** The same invoice as another photo. */
  'duplicate',
  /** A page with no sales on it (terms, a blank back). */
  'no_sales',
] as const;
export type PhotoLeftOut = (typeof PHOTO_LEFT_OUT)[number];

/** Fields a person may be asked to check on a photo. */
export const IMPORT_CHECK_FIELDS = [
  'invoiceNumber',
  'invoiceDate',
  'buyerGstin',
  'placeOfSupply',
  'hsn',
  'quantity',
  'rate',
  'taxableValue',
  'invoiceValue',
] as const;
export type ImportCheckField = (typeof IMPORT_CHECK_FIELDS)[number];

/** Why a field needs a look: the photo, or a check that didn't add up. */
export const IMPORT_CHECK_REASONS = [
  'blurred',
  'cut_off',
  'overwritten',
  'handwriting',
  'glare',
  'dark',
  /** The AI read it but wasn't sure. */
  'unsure',
  /** The AI couldn't see it at all. */
  'missing',
  /** A GSTIN whose last character doesn't match the rest. */
  'check_digit',
  /** The amounts on the invoice don't add up with this value. */
  'totals_differ',
] as const;
export type ImportCheckReason = (typeof IMPORT_CHECK_REASONS)[number];

/** What can be wrong with a row. */
export const IMPORT_PROBLEM_CODES = [
  'number_missing',
  'number_invalid',
  'number_used',
  'number_in_return',
  'invoice_rows_differ',
  'too_many_lines',
  'date_missing',
  'date_unreadable',
  'date_future',
  'date_after_period',
  'date_before_period',
  'gstin_invalid',
  'gstin_own',
  'pos_unreadable',
  'hsn_missing',
  'hsn_invalid',
  'rate_missing',
  'rate_unreadable',
  'rate_not_allowed',
  'rate_differs_hsn',
  'taxable_missing',
  'taxable_unreadable',
  'amount_negative',
  'amount_zero',
  'tax_mismatch',
  'tax_type_mismatch',
  'quantity_unreadable',
  'reverse_charge',
  'unsupported_type',
  'invoice_cancelled',
] as const;
export type ImportProblemCode = (typeof IMPORT_PROBLEM_CODES)[number];

/** use_fix: take the suggested fix; keep: leave it as it is (warnings only); edit: new values; skip: leave the row out. */
export const IMPORT_ACTIONS = ['use_fix', 'keep', 'edit', 'skip'] as const;
export type ImportAction = (typeof IMPORT_ACTIONS)[number];

export const IMPORT_ROW_STATUSES = ['ready', 'problem', 'duplicate', 'skipped', 'added'] as const;

/** A row's values in API units: amounts in paise, dates YYYY-MM-DD, places of supply as state codes. */
export const ImportRowFieldsSchema = z.object({
  invoiceNumber: z.string().nullable(),
  invoiceDate: z.string().nullable(),
  /** Null for a consumer. */
  buyerGstin: z.string().nullable(),
  buyerName: z.string().nullable(),
  placeOfSupply: z.string().nullable(),
  hsn: z.string().nullable(),
  description: z.string().nullable(),
  quantity: z.number().nullable(),
  unit: z.string().nullable(),
  rate: z.number().nullable(),
  taxableValue: z.number().int().nullable(),
  igst: z.number().int().nullable(),
  cgst: z.number().int().nullable(),
  sgst: z.number().int().nullable(),
  cess: z.number().int().nullable(),
  taxAmount: z.number().int().nullable(),
  invoiceValue: z.number().int().nullable(),
  reverseCharge: z.boolean().nullable(),
  invoiceType: z.string().nullable(),
  invoiceStatus: z.string().nullable(),
});
export type ImportRowFields = z.infer<typeof ImportRowFieldsSchema>;

/** Changes to a row: only the fields given change. */
export const ImportRowEditSchema = z.strictObject({
  invoiceNumber: z.string().trim().min(1).max(40).optional(),
  invoiceDate: isoDateString.optional(),
  /** Null: the buyer is a consumer. */
  buyerGstin: z.string().trim().max(20).nullable().optional(),
  /** A state code; null for the buyer's state (or, for a consumer, yours). */
  placeOfSupply: z
    .string()
    .regex(/^\d{2}$/)
    .nullable()
    .optional(),
  hsn: z.string().trim().max(12).optional(),
  description: z.string().trim().max(120).nullable().optional(),
  quantity: z.number().min(0).max(1e9).nullable().optional(),
  rate: z.number().min(0).max(100).optional(),
  /** Paise. */
  taxableValue: z.number().int().min(0).max(1e13).optional(),
});
export type ImportRowEdit = z.infer<typeof ImportRowEditSchema>;

export const ImportProblemSchema = z.object({
  id: z.string(),
  rowId: z.string(),
  /** The row in the file, as a spreadsheet numbers it, and its sheet when the sales are on several. */
  row: z.number().int(),
  sheet: z.string().nullable(),
  invoiceNumber: z.string().nullable(),
  code: z.enum(IMPORT_PROBLEM_CODES),
  /** The field it's about, or "row". */
  field: z.string(),
  /** error: the row waits until it's settled; warning: it can be kept as it is. */
  severity: z.enum(['error', 'warning']),
  /** What's wrong, in the person's language. */
  message: z.string(),
  params: z.record(z.string(), z.union([z.string(), z.number()])),
  /** The suggested fix: a short label ("Use 07AAKFD2210H1ZV") and the fields it sets. */
  fix: z.object({ label: z.string(), changes: ImportRowEditSchema }).nullable(),
  actions: z.array(z.enum(IMPORT_ACTIONS)),
});
export type ImportProblem = z.infer<typeof ImportProblemSchema>;

/** Photos: what was read, and how much is left for the person to check. */
export const ImportPhotosSchema = z.object({
  purchaseInvoices: z.number().int().optional(),
  /** Photos and PDF pages sent, and read so far (while it's reading). */
  pages: z.number().int(),
  read: z.number().int(),
  /** Invoices read from them. */
  invoices: z.number().int(),
  /** Invoices with a field to check, and the fields to check (all, and still open). */
  invoicesToCheck: z.number().int(),
  checks: z.number().int(),
  openChecks: z.number().int(),
  /** Photos not read as a sale, with why ("page" counts the photos and PDF pages from 1). */
  leftOut: z.array(z.object({ page: z.number().int(), reason: z.enum(PHOTO_LEFT_OUT), message: z.string() })),
});
export type ImportPhotos = z.infer<typeof ImportPhotosSchema>;

export const ImportCountsSchema = z.object({
  /** Rows read from the file. */
  rows: z.number().int(),
  ready: z.number().int(),
  problems: z.number().int(),
  skipped: z.number().int(),
  /** Already in the return, exactly so. */
  duplicates: z.number().int(),
  /** Invoices ready to add, and their value before tax (paise). */
  readyInvoices: z.number().int(),
  readyValue: z.number().int(),
  /** Rows whose rate or value before tax was worked out from the other columns. */
  derived: z.number().int(),
  /** Invoices added to the return from this file. */
  added: z.number().int(),
});
export type ImportCounts = z.infer<typeof ImportCountsSchema>;

export const ImportSchema = z.object({
  id: z.string(),
  returnId: z.string(),
  /** The file (sales files); photo imports list their photos with each invoice's checks. */
  uploadId: z.string().nullable(),
  kind: z.enum(IMPORT_KINDS),
  fileName: z.string(),
  fileSize: z.number().int(),
  status: z.enum(IMPORT_STATUSES),
  failure: z
    .object({
      reason: z.enum(IMPORT_FAILURES),
      message: z.string(),
      /** missing_columns: which. */
      missing: z.array(z.string()).optional(),
      rows: z.number().int().optional(),
    })
    .nullable(),
  counts: ImportCountsSchema,
  /** Which heading went to which field, the headings left out, and the sheets read (Excel). */
  columns: z
    .object({
      pasted: z.boolean().optional(),
      mapped: z.array(z.object({ header: z.string(), field: z.string() })),
      ignored: z.array(z.string()),
      sheets: z.array(z.string()),
    })
    .nullable(),
  /** Photo imports only. */
  photos: ImportPhotosSchema.nullable(),
  /** Statements only (Phase 16). */
  statement: z
    .object({
      /** The bank, when the statement names it. */
      bank: z.string().nullable(),
      /** The account's last 4 digits, when shown. */
      account: z.string().nullable(),
      /** Its first and last transactions' dates. */
      from: z.string().nullable(),
      to: z.string().nullable(),
      /** A PDF's pages. */
      pages: z.number().int().nullable(),
      /** Money in during the return's month: how many payments, and their total (paise). */
      credits: z.number().int(),
      moneyIn: z.number().int(),
      /** While it waits for its password: wrong ones left before it's given up. */
      triesLeft: z.number().int().nullable(),
      /** Groups still to ask about. */
      open: z.number().int(),
    })
    .nullable(),
  /** Problems still open, how many of each kind, and the first of them (all of them: GET …/problems). */
  openProblems: z.number().int(),
  openByCode: z.record(z.string(), z.number().int()),
  problems: z.array(ImportProblemSchema),
  version: z.number().int(),
  createdAt: z.string(),
  finishedAt: z.string().nullable(),
  appliedAt: z.string().nullable(),
});
export type Import = z.infer<typeof ImportSchema>;
export const ImportResponseSchema = z.object({ import: ImportSchema });

export const StartImportSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('sales_file'), uploadId: z.string().max(64) }),
  z.strictObject({ kind: z.literal('bank_statement'), uploadId: z.string().max(64) }),
  z.strictObject({
    kind: z.literal('invoice_photos'),
    /** The photos in order; a list inside the list is one PDF's pages (they may be one long invoice). */
    uploadIds: z
      .array(z.union([z.string().max(64), z.array(z.string().max(64)).min(1).max(MAX_PDF_PAGES)]))
      .min(1)
      .max(MAX_PHOTO_PAGES),
  }),
]);
export type StartImport = z.infer<typeof StartImportSchema>;

/**
 * Photos sent from the chat's photo sheet, as the widget's one text value: documents separated by
 * ";", a PDF's pages by "," ("a;b,c;d" is a photo, a two-page PDF, and a photo).
 */
export function encodePhotoUploads(entries: (string | string[])[]): string {
  return entries.map((entry) => (Array.isArray(entry) ? entry.join(',') : entry)).join(';');
}

export function decodePhotoUploads(text: string): (string | string[])[] {
  return text
    .split(';')
    .map((part) =>
      part
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean),
    )
    .filter((ids) => ids.length > 0)
    .map((ids) => (ids.length === 1 ? (ids[0] as string) : ids));
}

/** One field to check on a photo. */
export const ImportCheckSchema = z.object({
  id: z.string(),
  field: z.enum(IMPORT_CHECK_FIELDS),
  /** The invoice's line (from 1) for a line's field; null for the invoice's own. */
  line: z.number().int().nullable(),
  /** "Buyer’s GSTIN", "Taxable value · line 2". */
  label: z.string(),
  /**
   * As read, ready for the input: dates YYYY-MM-DD, amounts in rupees ("45000"), rates in percent,
   * places of supply as state codes. Null when nothing could be read.
   */
  value: z.string().nullable(),
  /** A better guess when a check found one (a GSTIN whose last character checks out). */
  suggestion: z.string().nullable(),
  reason: z.enum(IMPORT_CHECK_REASONS),
  /** What to compare, in the person's language. */
  hint: z.string(),
  /** Where it is on which photo (0–1 of its width and height), when the AI could tell. */
  area: z.object({ uploadId: z.string(), x: z.number(), y: z.number(), w: z.number(), h: z.number() }).nullable(),
  status: z.enum(['open', 'confirmed', 'corrected']),
  /** The value confirmed or typed (null while open). */
  answer: z.string().nullable(),
});
export type ImportCheck = z.infer<typeof ImportCheckSchema>;

/** An invoice with fields to check: what was read for sure, and what needs a look. */
export const ImportCheckInvoiceSchema = z.object({
  /** Its place among the invoices read (from 1), of `invoices`. */
  invoice: z.number().int(),
  invoices: z.number().int(),
  invoiceNumber: z.string().nullable(),
  buyerName: z.string().nullable(),
  /** The photos it's on, numbered from 1 as they were sent. */
  photos: z.array(z.object({ uploadId: z.string(), number: z.number().int() })),
  /** What was read for sure, to show with a tick. */
  sure: z.array(z.object({ label: z.string(), value: z.string() })),
  checks: z.array(ImportCheckSchema),
});
export type ImportCheckInvoice = z.infer<typeof ImportCheckInvoiceSchema>;
export const ImportChecksResponseSchema = z.object({
  import: ImportSchema,
  /** Invoices with an open check first, in the order they were read. */
  invoices: z.array(ImportCheckInvoiceSchema),
});
export type ImportChecksResponse = z.infer<typeof ImportChecksResponseSchema>;

/** The value as it is on the invoice: the one read, or the right one typed over it. */
export const ConfirmCheckSchema = z.strictObject({
  value: z.string().trim().max(60),
  /**
   * False from the chat's own check sheet: closing, it tells the chat itself (so the person's "done"
   * comes before what's next). Anywhere else the chat is told when the last check is done.
   */
  tellChat: z.boolean().optional(),
});
export type ConfirmCheck = z.infer<typeof ConfirmCheckSchema>;

/** A short-lived link to see a photo (the uploader's own). */
export const UploadViewSchema = z.object({ url: z.string(), expiresAt: z.string() });
export type UploadView = z.infer<typeof UploadViewSchema>;

export const ImportProblemListSchema = z.object({
  problems: z.array(ImportProblemSchema),
  nextCursor: z.string().nullable(),
});

export const ImportRowSchema = z.object({
  id: z.string(),
  /** Its row in the file; for photos, its invoice (from 1). */
  row: z.number().int(),
  sheet: z.string().nullable(),
  /** The cells as written in the file, under their headings (photos: as read off the photo). */
  cells: z.record(z.string(), z.string()),
  /** Photos: the photo it was read from. */
  uploadId: z.string().nullable(),
  /** As read from the file. */
  original: ImportRowFieldsSchema,
  /** With the fixes and edits since. */
  fields: ImportRowFieldsSchema,
  status: z.enum(IMPORT_ROW_STATUSES),
  problems: z.array(ImportProblemSchema),
});
export type ImportRow = z.infer<typeof ImportRowSchema>;
export const ImportRowsResponseSchema = z.object({
  import: ImportSchema,
  rows: z.array(ImportRowSchema),
  next: z.number().int().nullable(),
});
export const ImportRowResponseSchema = z.object({ import: ImportSchema, row: ImportRowSchema });

export const ResolveProblemSchema = z.strictObject({
  action: z.enum(IMPORT_ACTIONS),
  /** edit: the row's new values. */
  values: ImportRowEditSchema.optional(),
  /** Do the same for every open problem like this one (same kind): each with its own fix, for use_fix. */
  all: z.boolean().optional(),
});
export type ResolveProblem = z.infer<typeof ResolveProblemSchema>;

export const ApplyImportResponseSchema = z.object({
  import: ImportSchema,
  /** Invoices this call added (0 when repeated). */
  added: z.number().int(),
  return: ReturnDraftSchema,
});
export type ApplyImportResponse = z.infer<typeof ApplyImportResponseSchema>;

// ---------------------------------------------------------------------------
// No records (Phase 16): rebuilding a month with an owner who keeps no sales register. A bank or UPI
// statement is read (its password asked for and never kept), its money in sorted into groups the owner
// confirms; walk-in totals by rate, with their bill numbers, become one consumer entry in the return.
// GST follows the bills issued, not the money that came in: payments only help find walk-in sales.
//
//   POST  /api/v1/returns/:rid/imports                 { kind: bank_statement, uploadId } -> ImportResponse (202)
//   POST  /api/v1/imports/:id/password                 StatementPassword  -> ImportResponse (422: tries left)
//   GET   /api/v1/imports/:id/statement                                   -> StatementView
//   POST  /api/v1/imports/:id/statement-groups/:gid/confirm  DecideStatementGroup -> StatementView
//   PATCH /api/v1/imports/:id/statement-entries/:eid   MoveStatementEntry -> StatementView
//   GET   /api/v1/returns/:rid/walk-in                                    -> WalkInResponse
//   PUT   /api/v1/returns/:rid/walk-in                 PutWalkIn          -> WalkInResponse
//   DELETE /api/v1/returns/:rid/walk-in                                   -> WalkInResponse
//   POST  /api/v1/returns/:rid/walk-in/bills           CountBills         -> BillCount
// ---------------------------------------------------------------------------

/** How a payment came in, from its narration. */
export const STATEMENT_MODES = [
  'upi',
  'neft',
  'rtgs',
  'imps',
  'cash',
  'cheque',
  'card',
  /** A payment app's or gateway's settlement (PhonePe, Paytm, Razorpay…), after its fee. */
  'app',
  'interest',
  'reversal',
  'transfer',
  'loan',
  'other',
] as const;
export type StatementMode = (typeof STATEMENT_MODES)[number];

/**
 * bills: paid for this month's bills (shown, never added again); earlier: for an earlier month's bill
 * (left out: GST follows bills); walk_in: small payments from people; not_sales: own accounts, family,
 * interest, refunds, loans; cash: cash deposited; settlement: one app's settlements; unmatched: a larger
 * payment from a business with no bill for it. The last three are asked about one at a time.
 */
export const STATEMENT_GROUP_KINDS = [
  'bills',
  'earlier',
  'walk_in',
  'not_sales',
  'cash',
  'settlement',
  'unmatched',
] as const;
export type StatementGroupKind = (typeof STATEMENT_GROUP_KINDS)[number];

/** What the owner said: sales; not sales; some of it (cash); an advance; a sale whose bill they'll add. */
export const STATEMENT_DECISIONS = ['sales', 'not_sales', 'some', 'advance', 'add_bill'] as const;
export type StatementDecision = (typeof STATEMENT_DECISIONS)[number];

/** Where a payment can be moved by hand. */
export const STATEMENT_MOVES = ['walk_in', 'bills', 'not_sales'] as const;
export type StatementMove = (typeof STATEMENT_MOVES)[number];

/** Why a payment is probably not a sale. */
export const STATEMENT_NOTES = ['own_account', 'family', 'interest', 'reversal', 'refund', 'loan'] as const;
export type StatementNote = (typeof STATEMENT_NOTES)[number];

/** The most money-in payments a statement may have in a month. */
export const MAX_STATEMENT_ROWS = 10_000;
/** Wrong passwords before a statement is given up (and its file deleted). */
export const STATEMENT_PASSWORD_TRIES = 5;

export const StatementEntrySchema = z.object({
  id: z.string(),
  date: z.string(),
  /** Who paid, as the bank wrote it; null when the narration doesn't say. */
  payer: z.string().nullable(),
  mode: z.enum(STATEMENT_MODES),
  /** Paise, as it came into the bank. */
  amount: z.number().int(),
  groupId: z.string(),
  /** The bill it paid (bills, earlier). */
  bill: z.object({ number: z.string(), period: z.string() }).nullable(),
  note: z.enum(STATEMENT_NOTES).nullable(),
  /** Moved here by the owner. */
  moved: z.boolean(),
});
export type StatementEntry = z.infer<typeof StatementEntrySchema>;

export const StatementGroupSchema = z.object({
  id: z.string(),
  kind: z.enum(STATEMENT_GROUP_KINDS),
  /** The app (settlement) or the payer (unmatched); null for the rest. */
  label: z.string().nullable(),
  count: z.number().int(),
  /** Paise, as paid into the bank. */
  total: z.number().int(),
  /** open: still to ask about; decided: answered, or needs no answer. */
  status: z.enum(['open', 'decided']),
  decision: z.enum(STATEMENT_DECISIONS).nullable(),
  /** What of it is walk-in sales (paise): an app's own figure for what customers paid, cash's part. */
  sales: z.number().int().nullable(),
  /** Its payments' dates, in order (the first 10). */
  dates: z.array(z.string()),
  /** unmatched: how it came. */
  mode: z.enum(STATEMENT_MODES).nullable(),
});
export type StatementGroup = z.infer<typeof StatementGroupSchema>;

export const StatementViewSchema = z.object({
  import: ImportSchema,
  /** The return's month, YYYY-MM. */
  period: z.string(),
  /** In the order they're shown and asked about. */
  groups: z.array(StatementGroupSchema),
  /** Money in during the month, by date; empty once the return is filed (the totals stay). */
  entries: z.array(StatementEntrySchema),
  /** Walk-in sales found so far (paise): UPI and card payments, each app's figure, cash deposited. */
  walkIn: z.object({
    upiAndCard: z.number().int(),
    apps: z.array(z.object({ label: z.string(), sales: z.number().int() })),
    cash: z.number().int(),
    total: z.number().int(),
  }),
  /** The payments were erased when the return was filed. */
  erased: z.boolean(),
});
export type StatementView = z.infer<typeof StatementViewSchema>;

/** Used once to open the file; never stored or logged. */
export const StatementPasswordSchema = z.strictObject({ password: z.string().min(1).max(64) });
export type StatementPassword = z.infer<typeof StatementPasswordSchema>;

export const DecideStatementGroupSchema = z.strictObject({
  decision: z.enum(STATEMENT_DECISIONS),
  /** some (cash): how much was sales; sales (an app): what customers paid, by its report. Paise. */
  amount: z.number().int().min(0).max(1e13).optional(),
});
export type DecideStatementGroup = z.infer<typeof DecideStatementGroupSchema>;

export const MoveStatementEntrySchema = z.strictObject({ group: z.enum(STATEMENT_MOVES) });
export type MoveStatementEntry = z.infer<typeof MoveStatementEntrySchema>;

/** Walk-in sales at one rate: what customers paid, including GST (paise). */
export const WalkInRateInputSchema = z.strictObject({
  rate: z.number().min(0).max(100),
  paid: z.number().int().min(1).max(1e13),
});

export const BillRangeSchema = z.strictObject({
  from: z.string().trim().min(1).max(40),
  to: z.string().trim().min(1).max(40),
});
export type BillRange = z.infer<typeof BillRangeSchema>;

/** The most rates, bill books and cancelled bills one month's walk-in sales may have. */
export const WALK_IN_LIMITS = { rates: 8, ranges: 5, cancelled: 500 } as const;

export const PutWalkInSchema = z.strictObject({
  rates: z.array(WalkInRateInputSchema).min(1).max(WALK_IN_LIMITS.rates),
  /** First and last bill numbers, per bill book: the bills to registered buyers in between are left out. */
  bills: z.array(BillRangeSchema).min(1).max(WALK_IN_LIMITS.ranges),
  cancelled: z.array(z.string().trim().min(1).max(40)).max(WALK_IN_LIMITS.cancelled).optional(),
});
export type PutWalkIn = z.input<typeof PutWalkInSchema>;

export const WalkInSchema = z.object({
  invoiceId: z.string(),
  rates: z.array(AmountsSchema.extend({ rate: z.number(), paid: z.number().int(), tax: z.number().int() })),
  bills: z.array(z.object({ from: z.string(), to: z.string() })),
  cancelled: z.array(z.string()),
  counts: z.object({
    /** Numbers from first to last, in every bill book. */
    all: z.number().int(),
    /** Bills in between that are other sales in the return (registered buyers'). */
    others: z.number().int(),
    cancelled: z.number().int(),
    /** Walk-in bills: the rest. */
    walkIn: z.number().int(),
  }),
  totals: AmountsSchema.extend({ tax: z.number().int(), paid: z.number().int() }),
  version: z.number().int(),
  updatedAt: z.string(),
});
export type WalkIn = z.infer<typeof WalkInSchema>;
export const WalkInResponseSchema = z.object({ walkIn: WalkInSchema.nullable(), return: ReturnDraftSchema });
export type WalkInResponse = z.infer<typeof WalkInResponseSchema>;

/** Counting bills before saving (the walk-in page's note): first and last of each bill book. */
export const CountBillsSchema = z.strictObject({
  bills: z.array(BillRangeSchema).min(1).max(WALK_IN_LIMITS.ranges),
});
export const BillCountSchema = z.object({
  /** Written the way the series in use writes them ("2401" → "INV-2401"). */
  bills: z.array(z.object({ from: z.string(), to: z.string() })),
  all: z.number().int(),
  /** Other sales in the return already (registered buyers'). */
  others: z.number().int(),
});
export type BillCount = z.infer<typeof BillCountSchema>;

// ---------------------------------------------------------------------------
// Filing GSTR-1 (Phase 9): checked, confirmed with a fingerprint of what's filed, saved to the GST
// portal, signed with EVC and filed, as a background job the chat follows. Every route needs a
// signed-in person who added the business.
//
//   POST /api/v1/returns/:rid/validate                             -> FilingCheck
//   POST /api/v1/returns/:rid/confirm      ConfirmFiling           -> FilingJobResponse
//   POST /api/v1/returns/:rid/submit       SubmitFiling?           -> FilingJobResponse (202)
//   GET  /api/v1/returns/:rid/status                               -> FilingJobResponse
//   POST /api/v1/returns/:rid/sign/evc/otp                         -> FilingJobResponse
//   POST /api/v1/returns/:rid/sign/evc     SignWithEvc             -> FilingJobResponse
//   POST /api/v1/returns/:rid/cancel                               -> FilingJobResponse
//   PUT  /api/v1/businesses/:id/signatory  SetSignatory            -> SignatoryResponse
// ---------------------------------------------------------------------------

/**
 * confirmed → saving (to the portal) → saved (its summary matches) → signing (an EVC code is on its
 * way) → filing → filed; or failed, or cancelled. GSTR-3B (Phase 11) pays between saving and signing:
 * saving (and making the challan) → paying (until RBI confirms the payment; skipped when there's
 * nothing to pay) → offsetting (the tax set off from the ledgers, the breakup confirmed) → saved.
 */
export const FILING_JOB_STATUSES = [
  'confirmed',
  'saving',
  // GSTR-3B (Phase 11): the challan is waiting to be paid; the money's in and the tax is being set off.
  'paying',
  'offsetting',
  'saved',
  'signing',
  'filing',
  'filed',
  'failed',
  'needs_review',
  'cancelled',
] as const;
export type FilingJobStatus = (typeof FILING_JOB_STATUSES)[number];

/**
 * Why a filing stopped: the portal refused some sales; its summary didn't match EkApp's; it stayed
 * down; the portal session ended; the portal won't take this return yet; or it was filed elsewhere.
 * GSTR-3B: the challan couldn't be made, or the cash ledger had less than the tax needs when setting off.
 */
export const FILING_FAILURES = [
  'save_rejected',
  'summary_mismatch',
  'portal_down',
  'reconnect_needed',
  'not_allowed',
  'filed_elsewhere',
  // GSTR-3B (Phase 11): the GSP wouldn't make the challan; the cash ledger had less than the tax needs.
  'challan_refused',
  'cash_short',
  // NIL returns (Phase 12): the GST portal says the return isn't NIL (records saved, tax, credit, a late fee).
  'not_nil',
] as const;
export type FilingFailure = (typeof FILING_FAILURES)[number];

/** Why a return can't be filed yet. */
export const FILING_BLOCKERS = [
  'empty',
  'issues',
  'period_not_over',
  'time_barred',
  'already_filed',
  'previous_gstr1',
  'previous_gstr3b',
  'not_connected',
  'reconnect_needed',
  'signatory_needed',
  'portal_off',
  'format',
  // GSTR-3B (Phase 11): its GSTR-1 isn't filed; the two questions aren't answered; GSTR-2B isn't made yet.
  'gstr1_first',
  'not_answered',
  'gstr2b_not_ready',
  // NIL returns (Phase 12), why a return isn't NIL: sales in GSTR-1 (in EkApp or on the portal); GSTR-3B has
  // tax on sales, credit in GSTR-2B, or interest or a late fee from earlier to pay; reverse charge or credit
  // to reverse was answered yes.
  'has_sales',
  'sales_tax',
  'has_credit',
  'has_fees',
  'has_activity',
  // GSTR-1A (gap 4b): it isn't open for the month now; and for GSTR-3B, a GSTR-1A started and not filed.
  'gstr1a_not_open',
  'gstr1a_open',
] as const;
export type FilingBlockerCode = (typeof FILING_BLOCKERS)[number];

export const FilingBlockerSchema = z.object({
  code: z.enum(FILING_BLOCKERS),
  message: z.string(),
  /** The return to file first, when it's another (previous_gstr1: the earliest GSTR-1 not filed). */
  period: z.string().nullable(),
});
export type FilingBlocker = z.infer<typeof FilingBlockerSchema>;

/** Something the portal refused or that didn't match, traced back to a sale when it's about one. */
export const FilingProblemSchema = z.object({
  /** b2b, b2cl, b2cs, hsn, doc_issue… */
  section: z.string().nullable(),
  invoiceNumber: z.string().nullable(),
  invoiceId: z.string().nullable(),
  /** The portal's code, when it gave one. */
  code: z.string().nullable(),
  message: z.string(),
});
export type FilingProblem = z.infer<typeof FilingProblemSchema>;

/** Who signs with EVC: the authorised signatory as the portal has them. */
export const SignatorySchema = z.object({ name: z.string(), panMasked: z.string() });
export type Signatory = z.infer<typeof SignatorySchema>;

export const FilingEffectsSchema = z.object({
  saved_on_portal: z.string().optional(),
  challan_created: z.string().optional(),
  cash_seen: z.string().optional(),
  set_off_done: z.string().optional(),
  filed: z.string().optional(),
});
export type FilingEffects = z.infer<typeof FilingEffectsSchema>;

export const FilingJobSchema = z.object({
  effects: FilingEffectsSchema.optional(),
  id: z.string(),
  returnId: z.string(),
  businessId: z.string(),
  form: z.enum(['gstr1', 'gstr1a', 'gstr3b']),
  period: z.string(),
  status: z.enum(FILING_JOB_STATUSES),
  /** Sales in the return as confirmed (each consumer bill counted). */
  sales: z.number().int(),
  confirmedAt: z.string(),
  confirmedVia: z.enum(['button', 'typed']),
  /** While saving: the portal isn't answering, how many tries so far and when the next is. */
  portalDown: z.object({ attempts: z.number().int(), of: z.number().int(), nextAt: z.string() }).nullable(),
  failure: z.enum(FILING_FAILURES).nullable(),
  problems: z.array(FilingProblemSchema),
  signer: SignatorySchema.nullable(),
  /** The EVC code waiting (signing). */
  evc: z
    .object({
      sentTo: z.array(z.string()),
      sentAt: z.string(),
      expiresAt: z.string(),
      resendAt: z.string(),
      attemptsLeft: z.number().int(),
    })
    .nullable(),
  /** Three wrong codes pause signing until then. */
  lockedUntil: z.string().nullable(),
  arn: z.string().nullable(),
  filedAt: z.string().nullable(),
  /** For GET /filings/:id, once filed. */
  filingId: z.string().nullable(),
  /** GSTR-3B: what's paid by challan (0: nothing to pay), from which bank, and the challan for it (GET /challans/:id). */
  toPay: z.number().int().nullable(),
  bank: z.object({ code: z.string(), name: z.string() }).nullable(),
  challanId: z.string().nullable(),
  /**
   * GSTR-3B paid on the GST portal (step 2: a GSP that can't make challans): when the person said they'd paid,
   * when EkApp saw enough in the cash ledger, and when it looks next. Null otherwise.
   */
  portalPayment: z
    .object({
      /** What to pay on the portal's challan, head by head and minor head (paise, whole rupees). */
      amounts: z.object({
        tax: z.object({
          igst: z.number().int(),
          cgst: z.number().int(),
          sgst: z.number().int(),
          cess: z.number().int(),
        }),
        interest: z.object({
          igst: z.number().int(),
          cgst: z.number().int(),
          sgst: z.number().int(),
          cess: z.number().int(),
        }),
        fee: z.object({
          igst: z.number().int(),
          cgst: z.number().int(),
          sgst: z.number().int(),
          cess: z.number().int(),
        }),
        total: z.number().int(),
      }),
      paidAt: z.string().nullable(),
      seenAt: z.string().nullable(),
      nextCheckAt: z.string().nullable(),
    })
    .nullable()
    .optional(),
  /** The fixed OTP of a test portal (the twin), for hints. */
  testOtp: z.string().nullable(),
  /** Filing it NIL (Phase 12). */
  nil: z.boolean(),
  version: z.number().int(),
});
export type FilingJob = z.infer<typeof FilingJobSchema>;

/** POST /returns/:rid/paid-on-portal (step 2): whether the cash ledger covers GSTR-3B now, and what's short. */
export const PortalPaymentCheckSchema = z.object({
  covered: z.boolean(),
  /** How much more the cash ledger needs (paise; 0 when it covers it). */
  short: z.number().int(),
  filing: FilingJobSchema,
});
export type PortalPaymentCheck = z.infer<typeof PortalPaymentCheckSchema>;

export const FilingCheckSchema = z.object({
  /** Nothing in the way: it can be confirmed. */
  ready: z.boolean(),
  blockers: z.array(FilingBlockerSchema),
  /** The fingerprint to confirm (of the return as the portal will get it). */
  summaryHash: z.string(),
  summary: Gstr1SummarySchema,
  dueOn: z.string(),
  /** Days past the due date so far, and the late fee they come to (paise). */
  daysLate: z.number().int(),
  lateFee: z.number().int(),
  lateFeePerDay: z.number().int(),
  signer: SignatorySchema.nullable(),
  /** The filing on its way, if any. */
  filing: FilingJobSchema.nullable(),
  return: ReturnDraftSchema,
});
export type FilingCheck = z.infer<typeof FilingCheckSchema>;

export const FilingJobResponseSchema = z.object({ filing: FilingJobSchema.nullable(), return: ReturnDraftSchema });
export type FilingJobResponse = z.infer<typeof FilingJobResponseSchema>;

export const ConfirmFilingSchema = z.strictObject({
  summaryHash: z.string().regex(/^[a-f0-9]{64}$/, { error: 'must be the summaryHash from validate' }),
  /** File it NIL (Phase 12): summaryHash is then the one from GET /returns/:rid/nil. */
  nil: z.boolean().optional(),
  /** How the person confirmed: the button, or typing FILE (GSTR-3B: typing PAY). */
  via: z.enum(['button', 'typed']),
  /** GSTR-1A (gap 4b): they agree the month's GSTR-3B takes its figures, as the GST portal asks before filing. */
  declared: z.boolean().optional(),
  /** GSTR-3B with something to pay: the bank it's paid from by NEFT or RTGS (GET /neft-banks), e.g. HDFC. */
  bankCode: z
    .string()
    .trim()
    .transform((v) => v.toUpperCase())
    .pipe(z.string().regex(/^[A-Z]{4}$/, { error: 'must be a bank code from GET /neft-banks, like HDFC' }))
    .optional(),
});
export type ConfirmFiling = z.infer<typeof ConfirmFilingSchema>;

/**
 * Whether a return can be filed NIL now (Phase 12: GET /returns/:rid/nil), and if not, why: what's in the way
 * of filing it at all, or what the month has that NIL can't (blockers with has_… codes). Amounts in paise.
 */
export const NilCheckSchema = z.object({
  form: z.enum(['gstr1', 'gstr3b']),
  period: z.string(),
  /** It can be confirmed NIL now. */
  nil: z.boolean(),
  blockers: z.array(FilingBlockerSchema),
  /** The fingerprint to confirm the NIL filing with. */
  summaryHash: z.string(),
  dueOn: z.string(),
  /** Days past the due date so far, and the late fee at the NIL rate (₹20 a day, at most ₹500). */
  daysLate: z.number().int(),
  lateFee: z.number().int(),
  lateFeePerDay: z.number().int(),
  signer: SignatorySchema.nullable(),
  /**
   * GSTR-1: sales added in EkApp for the month, and since gap 4 its credit and debit notes and GST-free
   * sales (paise): any of them makes it a usual GSTR-1.
   */
  sales: z
    .object({
      invoices: z.number().int(),
      taxable: z.number().int(),
      notes: z.number().int(),
      gstFree: z.number().int(),
    })
    .nullable(),
  /**
   * GSTR-3B: what the portal has for the month once GSTR-2B is made: tax on sales from GSTR-1, credit in
   * GSTR-2B, and interest and late fee from earlier months (Table 5.1). Null until GSTR-2B is made.
   */
  figures: z.object({ salesTax: z.number().int(), credit: z.number().int(), fees: z.number().int() }).nullable(),
  /** GSTR-3B: when GSTR-2B for the month is made (the 14th of the next month), while it isn't yet. */
  gstr2bOn: z.string().nullable(),
  /** The return to file: GSTR-1's or GSTR-3B's for the same month. */
  returnId: z.string(),
  /** The filing on its way (or the last one), if any. */
  filing: FilingJobSchema.nullable(),
});
export type NilCheck = z.infer<typeof NilCheckSchema>;

export const SubmitFilingSchema = z
  .strictObject({
    /** Clear what the portal has for the month first, then save EkApp's (after the portal's didn't match). */
    replace: z.boolean().optional(),
  })
  .nullish();
export type SubmitFiling = z.infer<typeof SubmitFilingSchema>;

/** An EVC code: 6 characters, letters as well as digits (GSTN's EVC codes for filing have both, seen live). */
export const EVC_CODE = /^[A-Za-z0-9]{6}$/;

export const SignWithEvcSchema = z.strictObject({
  otp: z.string().trim().regex(EVC_CODE, { error: 'must be the 6-character code' }),
});
export type SignWithEvc = z.infer<typeof SignWithEvcSchema>;

export const SetSignatorySchema = z.strictObject({
  name: z.string().trim().min(2).max(100),
  /** The authorised signatory's PAN, as the GST portal has it. */
  pan: z
    .string()
    .trim()
    .transform((v) => v.toUpperCase())
    .pipe(z.string().regex(/^[A-Z]{5}\d{4}[A-Z]$/, { error: 'must be a PAN, like ABCDE1234F' })),
});
export type SetSignatory = z.infer<typeof SetSignatorySchema>;

export const SignatoryResponseSchema = z.object({ signatory: SignatorySchema });

// ---------------------------------------------------------------------------
// GSTR-3B (Phase 10): filled from the GST portal (tax on sales from GSTR-1, locked; credit from
// GSTR-2B; the cash ledger; the previous month's interest and late fee), the two things only the
// person knows (reverse-charge purchases, credit to reverse), and what's to pay. Money in paise.
//
//   POST /api/v1/businesses/:id/returns    { form: 'gstr3b', period }  -> Gstr3bReturnResponse
//   GET  /api/v1/returns/:rid              (a GSTR-3B)                  -> Gstr3bReturnResponse
//   PUT  /api/v1/returns/:rid/rcm-entries  PutReverseCharge            -> Gstr3bReturnResponse
//   PUT  /api/v1/returns/:rid/itc-reversals PutReversals               -> Gstr3bReturnResponse
//   GET  /api/v1/returns/:rid/computation                              -> Gstr3bComputationResponse
//   GET  /api/v1/businesses/:id/ledgers    ?refresh=true               -> LedgersResponse
//   GET  /api/v1/businesses/:id/gstr2b     ?period=YYYY-MM             -> Gstr2bResponse
// ---------------------------------------------------------------------------

const paise = z.number().int();

/** An amount for each tax head, in paise. */
export const ByHeadSchema = z.object({ igst: paise, cgst: paise, sgst: paise, cess: paise });
export type ByHeadAmounts = z.infer<typeof ByHeadSchema>;

const inputPaise = z.number().int().min(0).max(1e13);
export const ByHeadInputSchema = z.strictObject({
  igst: inputPaise.default(0),
  cgst: inputPaise.default(0),
  sgst: inputPaise.default(0),
  cess: inputPaise.default(0),
});

export const ReverseChargeEntryInputSchema = z.strictObject({
  kind: z.enum(REVERSE_CHARGE_KINDS),
  /** Before tax, in paise. */
  taxableValue: z.number().int().min(100).max(1e12),
  /** The supplier is in another state: IGST. Services from abroad are IGST anyway. */
  interState: z.boolean().optional(),
  supplier: z.string().trim().max(100).nullish(),
});
export type ReverseChargeEntryInput = z.input<typeof ReverseChargeEntryInputSchema>;

export const PutReverseChargeSchema = z.strictObject({
  /** All of the month's reverse-charge purchases (none: answered "no"). */
  entries: z.array(ReverseChargeEntryInputSchema).max(50),
});
export type PutReverseCharge = z.input<typeof PutReverseChargeSchema>;

export const ReversalInputSchema = z
  .strictObject({ reason: z.enum(REVERSAL_REASONS), amounts: ByHeadInputSchema })
  .refine((r) => r.amounts.igst + r.amounts.cgst + r.amounts.sgst + r.amounts.cess > 0, {
    error: 'reverse something, or leave it out',
    path: ['amounts'],
  });
export type ReversalInput = z.input<typeof ReversalInputSchema>;

export const PutReversalsSchema = z.strictObject({
  /** All of the month's credit to reverse (none: answered "no"). */
  reversals: z.array(ReversalInputSchema).max(20),
});
export type PutReversals = z.input<typeof PutReversalsSchema>;

export const ReverseChargeEntrySchema = z.object({
  kind: z.enum(REVERSE_CHARGE_KINDS),
  taxableValue: paise,
  interState: z.boolean(),
  supplier: z.string().nullable(),
  /** The tax on it, paid in cash and claimed back as credit. */
  tax: ByHeadSchema,
});
export type ReverseChargeEntryView = z.infer<typeof ReverseChargeEntrySchema>;

export const ReversalSchema = z.object({
  reason: z.enum(REVERSAL_REASONS),
  amounts: ByHeadSchema,
  /** For good (Table 4B(1)), or to claim back later (4B(2)). */
  permanent: z.boolean(),
});
export type ReversalView = z.infer<typeof ReversalSchema>;

export const GSTR2B_INELIGIBLE_REASONS = ['place_of_supply', 'time_limit', 'other'] as const;

/** GSTR-2B for a month: the credit the portal says can be claimed, and what can't (with why). */
export const Gstr2bSchema = z.object({
  period: z.string(),
  /** When the portal made it (the 14th of the next month); null when it isn't ready yet. */
  generatedOn: z.string().nullable(),
  /** Purchase bills in it. */
  documents: z.number().int(),
  eligible: ByHeadSchema,
  ineligible: ByHeadSchema,
  ineligibleBy: z.array(
    z.object({ reason: z.enum(GSTR2B_INELIGIBLE_REASONS), documents: z.number().int(), credit: ByHeadSchema }),
  ),
  fetchedAt: z.string(),
});
export type Gstr2b = z.infer<typeof Gstr2bSchema>;
export const Gstr2bResponseSchema = z.object({ gstr2b: Gstr2bSchema });

/** The electronic cash and credit ledgers' balances. */
export const LedgersSchema = z.object({
  cash: z.object({ tax: ByHeadSchema, interest: ByHeadSchema, fee: ByHeadSchema }),
  credit: ByHeadSchema,
  fetchedAt: z.string(),
});
export type Ledgers = z.infer<typeof LedgersSchema>;
export const LedgersResponseSchema = z.object({ ledgers: LedgersSchema });

export const Gstr3bReturnSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  form: z.literal('gstr3b'),
  period: z.string(),
  status: z.enum(['draft', 'filing', 'filed']),
  version: z.number().int(),
  dueOn: z.string(),
  /** What the portal filled in; null until it's been read. */
  portal: z
    .object({
      /** Tax on sales from GSTR-1 (Table 3.1), locked: changes go through GSTR-1A. */
      sales: ByHeadSchema,
      /** Credit from GSTR-2B (Table 4A). */
      credit: ByHeadSchema,
      ineligible: ByHeadSchema,
      ineligibleBy: Gstr2bSchema.shape.ineligibleBy,
      documents: z.number().int(),
      /** Interest and late fee for the previous month (Table 5.1), as the portal drafted them. */
      previous: z.object({ period: z.string(), interest: ByHeadSchema, lateFee: ByHeadSchema }),
      /** Gap 4b: tax on credit notes buyers rejected in IMS, added back by the portal (in `sales` already). */
      addedBack: ByHeadSchema.nullable(),
      rule37a: z
        .object({ amounts: ByHeadSchema, suppliers: z.array(z.string()), generatedOn: z.string().nullable() })
        .optional(),
      fetchedAt: z.string(),
    })
    .nullable(),
  reverseCharge: z.object({ answered: z.boolean(), entries: z.array(ReverseChargeEntrySchema) }),
  reversals: z.object({ answered: z.boolean(), items: z.array(ReversalSchema) }),
  updatedAt: z.string(),
});
export type Gstr3bReturn = z.infer<typeof Gstr3bReturnSchema>;
export const Gstr3bReturnResponseSchema = z.object({ return: Gstr3bReturnSchema });

const CashByMinorHeadSchema = z.object({ tax: ByHeadSchema, interest: ByHeadSchema, fee: ByHeadSchema });

/** What GSTR-3B comes to: credit used in the order the law sets, the cash ledger, and what's to pay now. */
export const Gstr3bComputationSchema = z.object({
  period: z.string(),
  sales: ByHeadSchema,
  reverseCharge: ByHeadSchema,
  credit: z.object({
    available: ByHeadSchema,
    reversedForGood: ByHeadSchema,
    reversedForNow: ByHeadSchema,
    net: ByHeadSchema,
  }),
  /** Each use of credit: so much of one head's credit for so much of a head's tax. */
  used: z.array(z.object({ from: z.enum(TAX_HEADS), to: z.enum(TAX_HEADS), amount: paise })),
  creditUsed: ByHeadSchema,
  creditLeft: ByHeadSchema,
  cashTax: ByHeadSchema,
  previous: z.object({ period: z.string(), interest: ByHeadSchema, lateFee: ByHeadSchema }),
  fromLedger: CashByMinorHeadSchema,
  toPay: CashByMinorHeadSchema,
  totals: z.object({ tax: paise, creditUsed: paise, previous: paise, fromLedger: paise, toPay: paise }),
  /** Filed late: this month's own interest and late fee so far, which the portal adds to the next month's GSTR-3B. */
  late: z
    .object({ days: z.number().int(), interest: ByHeadSchema, lateFee: ByHeadSchema, collectedIn: z.string() })
    .nullable(),
  dueOn: z.string(),
  /** Worked out as if filed today (India's date). */
  asOf: z.string(),
  ledgersFetchedAt: z.string(),
});
export type Gstr3bComputation = z.infer<typeof Gstr3bComputationSchema>;
export const Gstr3bComputationResponseSchema = z.object({ computation: Gstr3bComputationSchema });

// ---------------------------------------------------------------------------
// Paying and filing GSTR-3B (Phase 11): confirmed with the bank it's paid from, saved on the GST portal
// and checked against EkApp's working, a challan (PMT-06) made through the GSP for what's left to pay,
// paid by NEFT or RTGS at the person's bank and watched until RBI confirms it, then the tax set off, the
// liability breakup confirmed, and the return signed with EVC and filed (Phase 9's signing). Tax money
// goes from the person's bank to the Government's GST account at RBI, never through EkApp.
//
//   POST /api/v1/returns/:rid/validate      (a GSTR-3B)                 -> Gstr3bFilingCheck
//   POST /api/v1/returns/:rid/confirm       ConfirmFiling (+ bankCode)  -> Gstr3bFilingResponse
//   POST /api/v1/returns/:rid/submit                                    -> Gstr3bFilingResponse (202)
//   GET  /api/v1/returns/:rid/status                                    -> Gstr3bFilingResponse
//   POST /api/v1/returns/:rid/challans      CreateChallan               -> ChallanResponse (201)
//   GET  /api/v1/challans/:id                                           -> ChallanResponse
//   POST /api/v1/challans/:id/refresh                                   -> ChallanResponse
//   POST /api/v1/challans/:id/payment-session                           -> PaymentSessionResponse
//   POST /api/v1/challans/:id/paid                                      -> ChallanResponse
//   POST /api/v1/returns/:rid/offset                                    -> Gstr3bFilingResponse
//   GET  /api/v1/neft-banks                 ?q=                         -> NeftBanksResponse
//   POST /api/v1/returns/:rid/sign/evc/otp, /sign/evc, /cancel          (Phase 9's, for GSTR-3B too)
// ---------------------------------------------------------------------------

/** Where a challan stands: not paid; with the banks; paid (CIN); the bank's transfer failed; expired. */
export const CHALLAN_STATES = ['unpaid', 'pending', 'paid', 'failed', 'expired'] as const;
export type ChallanStateName = (typeof CHALLAN_STATES)[number];

export const NeftBankSchema = z.object({
  /** The four letters its IFSCs start with: what a challan names. */
  code: z.string(),
  name: z.string(),
});
export type NeftBankView = z.infer<typeof NeftBankSchema>;
export const NeftBanksResponseSchema = z.object({ banks: z.array(NeftBankSchema) });

export const ChallanSchema = z.object({
  id: z.string(),
  returnId: z.string(),
  businessId: z.string(),
  period: z.string(),
  /** The challan's number on the portal: the NEFT/RTGS account number. */
  cpin: z.string(),
  /** Paid at the bank by NEFT, or RTGS from ₹2 lakh. */
  mode: z.literal('neft'),
  bank: NeftBankSchema,
  /** By tax head and minor head, whole rupees (in paise). */
  amounts: z.object({ tax: ByHeadSchema, interest: ByHeadSchema, fee: ByHeadSchema }),
  total: paise,
  state: z.enum(CHALLAN_STATES),
  /** GSTN's own status code (N, S, K, X…). */
  portalStatus: z.string(),
  createdAt: z.string(),
  /** Its last day (15 days, the first the day it was made). */
  expiresOn: z.string(),
  /** When the person said they'd sent it. */
  paidAtBankAt: z.string().nullable(),
  /** Paid: the CIN RBI gave it, the bank's reference, the day. */
  receipt: z.object({ cin: z.string(), bankRef: z.string().nullable(), paidOn: z.string().nullable() }).nullable(),
  lastCheckedAt: z.string().nullable(),
  nextCheckAt: z.string().nullable(),
  /** Sent more than two hours ago and RBI hasn't confirmed it: what to check at the bank. */
  overdue: z.boolean(),
  version: z.number().int(),
});
export type Challan = z.infer<typeof ChallanSchema>;
export const ChallanResponseSchema = z.object({ challan: ChallanSchema });

export const CreateChallanSchema = z.strictObject({
  /** The bank it's paid from (GET /neft-banks), e.g. HDFC. */
  bankCode: z
    .string()
    .trim()
    .transform((v) => v.toUpperCase())
    .pipe(z.string().regex(/^[A-Z]{4}$/, { error: 'must be a bank code from GET /neft-banks, like HDFC' })),
});
export type CreateChallan = z.infer<typeof CreateChallanSchema>;

/** How to pay a challan at the bank: what to send, and where. */
export const PaymentSessionSchema = z.object({
  challanId: z.string(),
  /** Exactly this, in one payment. */
  amount: paise,
  /** NEFT, and RTGS from ₹2 lakh. */
  modes: z.array(z.enum(['neft', 'rtgs'])),
  /** The Government's GST account at RBI: the CPIN is the account number. */
  beneficiary: z.object({ name: z.string(), accountNumber: z.string(), ifsc: z.string(), bank: z.string() }),
  /** The bank on the challan: the money must come from it. */
  fromBank: NeftBankSchema,
  expiresOn: z.string(),
});
export type PaymentSession = z.infer<typeof PaymentSessionSchema>;
export const PaymentSessionResponseSchema = z.object({ payment: PaymentSessionSchema, challan: ChallanSchema });

/** Whether a GSTR-3B can be paid and filed now, and what's in the way. */
export const Gstr3bFilingCheckSchema = z.object({
  ready: z.boolean(),
  blockers: z.array(FilingBlockerSchema),
  /** The fingerprint to confirm: of the return as it will be saved and the amounts as worked out now. */
  summaryHash: z.string(),
  computation: Gstr3bComputationSchema.nullable(),
  /** What the challan will be for (whole rupees), 0 when there's nothing to pay. */
  toPay: paise,
  signer: SignatorySchema.nullable(),
  filing: FilingJobSchema.nullable(),
  return: Gstr3bReturnSchema,
});
export type Gstr3bFilingCheck = z.infer<typeof Gstr3bFilingCheckSchema>;

export const Gstr3bFilingResponseSchema = z.object({ filing: FilingJobSchema.nullable(), return: Gstr3bReturnSchema });
export type Gstr3bFilingResponse = z.infer<typeof Gstr3bFilingResponseSchema>;

// ---------------------------------------------------------------------------
// Receipts, reminders and notifications (Phase 13). Every filing keeps a PDF record made by EkApp from
// what the portal confirmed (GSTN gives apps no acknowledgement PDF), and so does every paid challan. The
// bell lists notices: reminders (3 days before each due date, on the day, Mondays while late, at 9:30 am
// India time) and news (payment received, filed, waiting to be signed, a challan's last day, the portal
// connection ending). Each notice goes out once per channel the person has on: push (Firebase Cloud
// Messaging) and email; every email can be stopped in one tap, without signing in.
//
//   GET    /api/v1/notifications                 ?cursor, limit     -> NotificationList
//   POST   /api/v1/notifications/:id/read                           -> NotificationResponse
//   POST   /api/v1/notifications/read-all                           -> ReadAllResponse
//   GET    /api/v1/me/notification-preferences                      -> NotificationPreferences
//   PUT    /api/v1/me/notification-preferences    UpdateNotificationPreferences -> NotificationPreferences
//   POST   /api/v1/me/notification-preferences/test                 -> TestReminderResponse (202)
//   POST   /api/v1/me/devices                     RegisterDevice    -> DeviceResponse (201)
//   DELETE /api/v1/me/devices/:id                                   -> 204
//   GET    /api/v1/unsubscribe                    ?token (no sign-in) -> UnsubscribeInfo
//   POST   /api/v1/unsubscribe                    ?token (no sign-in; RFC 8058 one-click) -> UnsubscribeInfo
//   GET    /api/v1/filings/:id/acknowledgement                      -> DocumentLink (the filing record PDF)
//   GET    /api/v1/challans/:id/record                              -> DocumentLink (the challan record PDF)
//   POST   /api/v1/admin/reminders/run            RunReminders (admin token) -> RemindersRun
// ---------------------------------------------------------------------------

/**
 * reminder_soon / _today / _late: a return due in 3 days, today, or late (Mondays). payment_received: RBI
 * confirmed a challan (sign GSTR-3B now). filed: a return went through. needs_signing: saved on the portal
 * and waiting for the EVC code. challan_last_day: a challan can be paid until tomorrow only.
 * connection_ending: the GST portal connection (API access) ends soon. test: "Send me a test reminder".
 */
export const NOTIFICATION_KINDS = [
  'reminder_soon',
  'reminder_today',
  'reminder_late',
  'payment_received',
  'filed',
  'needs_signing',
  'challan_last_day',
  'connection_ending',
  'test',
  // Phase 14. approval_requested / _reminder: to owners; approval_sent_back / _approved: to the staff who
  // sent it; member_joined / member_connected: to owners (someone joined by invite / by connecting the GST
  // account); member_removed: to the person removed.
  'approval_requested',
  'approval_reminder',
  'approval_sent_back',
  'approval_approved',
  'member_joined',
  'member_connected',
  'member_removed',
  // Phase 17. export_ready: a download is made; deletion_scheduled / deletion_kept: the account is set to be
  // deleted, or kept; privacy_request_received / _answered: asking about your data or complaining; breach:
  // a personal data breach that affects the person.
  'export_ready',
  'deletion_scheduled',
  'deletion_kept',
  'privacy_request_received',
  'privacy_request_answered',
  'breach',
  // Gap 4b. note_rejected: a buyer rejected a credit note in IMS, so its tax comes back in a GSTR-3B (to owners).
  'note_rejected',
  // Step 2. cash_arrived: GSTR-3B's tax, paid on the GST portal, is in the cash ledger: set off and sign.
  'cash_arrived',
  'filing_stopped',
] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

export const NotificationSchema = z.object({
  id: z.string(),
  kind: z.enum(NOTIFICATION_KINDS),
  businessId: z.string().nullable(),
  /** In the person's language. */
  title: z.string(),
  body: z.string(),
  /** Where tapping it goes, inside EkApp (a path). */
  link: z.string(),
  /** Its button when it asks for something ("File GSTR-3B · Sep", "Sign and file"). */
  action: z.string().nullable(),
  createdAt: z.string(),
  readAt: z.string().nullable(),
});
export type NotificationView = z.infer<typeof NotificationSchema>;

export const NotificationListSchema = z.object({
  /** Newest first, the last 90 days. */
  notifications: z.array(NotificationSchema),
  nextCursor: z.string().nullable(),
  /** How many are unread, all told (the bell's badge). */
  unread: z.number().int(),
});
export type NotificationList = z.infer<typeof NotificationListSchema>;
export const NotificationResponseSchema = z.object({ notification: NotificationSchema, unread: z.number().int() });
export const ReadAllResponseSchema = z.object({ unread: z.literal(0) });

/** A browser that gets push notifications (its Firebase Cloud Messaging token stays on the server). */
export const DeviceSchema = z.object({
  id: z.string(),
  /** "Chrome on Android", as the browser described itself. */
  label: z.string().nullable(),
  createdAt: z.string(),
  lastSeenAt: z.string(),
});
export type Device = z.infer<typeof DeviceSchema>;
export const DeviceResponseSchema = z.object({ device: DeviceSchema });

export const RegisterDeviceSchema = z.strictObject({
  /** The FCM registration token from the web app (firebase/messaging getToken). */
  token: z.string().trim().min(20).max(4096),
  label: z.string().trim().max(80).optional(),
});
export type RegisterDevice = z.infer<typeof RegisterDeviceSchema>;

/** A return's next due date, for a business on the reminders page. */
export const ReminderNextSchema = z.object({
  form: z.enum(RETURN_FORMS),
  period: z.string(),
  periodKind: z.enum(PERIOD_KINDS),
  dueOn: z.string(),
  state: z.enum(['late', 'upcoming']),
  daysLate: z.number().int().nullable(),
  daysLeft: z.number().int().nullable(),
});

export const NotificationPreferencesSchema = z.object({
  push: z.object({
    /** Push is set up on this server (Firebase Cloud Messaging); the browser still has to allow it. */
    available: z.boolean(),
    on: z.boolean(),
    devices: z.array(DeviceSchema),
  }),
  email: z.object({
    /** From the profile; null: none given, so no email reminders. */
    address: z.string().nullable(),
    on: z.boolean(),
  }),
  /** Due-date reminders for each business, on unless turned off. */
  businesses: z.array(
    z.object({ businessId: z.string(), name: z.string(), on: z.boolean(), next: ReminderNextSchema.nullable() }),
  ),
});
export type NotificationPreferences = z.infer<typeof NotificationPreferencesSchema>;

export const UpdateNotificationPreferencesSchema = z.strictObject({
  push: z.boolean().optional(),
  email: z.boolean().optional(),
  businesses: z
    .array(z.strictObject({ businessId: z.string(), on: z.boolean() }))
    .max(100)
    .optional(),
});
export type UpdateNotificationPreferences = z.infer<typeof UpdateNotificationPreferencesSchema>;

export const TestReminderResponseSchema = z.object({
  notification: NotificationSchema,
  /** Where it's going: how many browsers, and whether an email was sent. */
  sentTo: z.object({ devices: z.number().int(), email: z.boolean() }),
});
export type TestReminderResponse = z.infer<typeof TestReminderResponseSchema>;

/** What a link from a reminder email turns off (or has turned off). */
export const UnsubscribeInfoSchema = z.object({
  channel: z.literal('email'),
  /** The business whose reminders it's about; null: every reminder email. */
  businessName: z.string().nullable(),
  done: z.boolean(),
});
export type UnsubscribeInfo = z.infer<typeof UnsubscribeInfoSchema>;

/** A short-lived link to one of EkApp's PDF records. */
export const DocumentLinkSchema = z.object({
  url: z.string(),
  fileName: z.string(),
  expiresAt: z.string(),
});
export type DocumentLink = z.infer<typeof DocumentLinkSchema>;

/** Run the reminders now (admin, the QA panel): outside 9:30 am to 9 pm too with force; some people only. */
export const RunRemindersSchema = z.strictObject({
  force: z.boolean().optional(),
  userIds: z.array(z.string().max(64)).max(100).optional(),
});
export type RunReminders = z.infer<typeof RunRemindersSchema>;
/** How many people were looked at, and how many reminders were made (each goes once a day at most). */
export const RemindersRunSchema = z.object({ checked: z.number().int(), reminded: z.number().int() });
export type RemindersRun = z.infer<typeof RemindersRunSchema>;

// ---------------------------------------------------------------------------
// People, invites and approvals (Phase 14). Owners approve, pay, sign (with an OTP on their own mobile),
// file and manage people; staff prepare and send returns for approval. An invite is for one mobile number,
// works once, for 7 days; its link carries a token only its hash is kept of. An approval request keeps a
// fingerprint of the return: approving runs the filing as the owner, any change withdraws the request.
//
//   GET    /api/v1/businesses/:id/members                        -> People
//   PATCH  /api/v1/businesses/:id/members/:userId  UpdateMember  -> MemberResponse (owners only)
//   DELETE /api/v1/businesses/:id/members/:userId                -> 204 (owners; anyone, for themselves)
//   POST   /api/v1/businesses/:id/invites          CreateInvite  -> InviteResponse (201, with the link)
//   DELETE /api/v1/businesses/:id/invites/:inviteId              -> 204
//   GET    /api/v1/invites/:token                                -> InvitePreview
//   POST   /api/v1/invites/:token/accept                         -> BusinessResponse
//   POST   /api/v1/returns/:rid/approval-requests                -> ApprovalResponse (201; staff)
//   GET    /api/v1/approval-requests               ?status       -> ApprovalList
//   GET    /api/v1/approval-requests/:id                         -> ApprovalResponse
//   POST   /api/v1/approval-requests/:id/approve                 -> ApproveResponse (owners)
//   POST   /api/v1/approval-requests/:id/send-back SendBack      -> ApprovalResponse (owners)
//   POST   /api/v1/approval-requests/:id/remind                  -> ApprovalResponse (the sender; once an hour)
// ---------------------------------------------------------------------------

/** An invite link works this long, once. */
export const INVITE_VALID_DAYS = 7;
/** Staff can remind owners about a request this often. */
export const REMIND_EVERY_MIN = 60;
export const APPROVAL_COMMENT_MAX = 500;

/** portal: connected the GST account (and so is an owner); invite: joined by an owner's invite. */
export const JOINED_VIA = ['portal', 'invite'] as const;

export const MemberSchema = z.object({
  userId: z.string(),
  name: z.string().nullable(),
  /** Masked, e.g. "+91 98450 ••321". */
  phone: z.string(),
  role: z.enum(BUSINESS_ROLES),
  you: z.boolean(),
  joinedVia: z.enum(JOINED_VIA),
  joinedAt: z.string(),
  invitedBy: z.string().nullable(),
  /** Made the business's GST portal connection. */
  connected: z.boolean(),
});
export type Member = z.infer<typeof MemberSchema>;

export const InviteSchema = z.object({
  id: z.string(),
  role: z.enum(BUSINESS_ROLES),
  /** The number it's for, masked. */
  phone: z.string(),
  createdAt: z.string(),
  expiresAt: z.string(),
  createdBy: z.string().nullable(),
  /** The link to share: only in the answer that made it (the token behind it isn't kept). */
  url: z.string().nullable(),
});
export type Invite = z.infer<typeof InviteSchema>;

export const PeopleSchema = z.object({
  /** Owners first, then staff; each by when they joined. */
  members: z.array(MemberSchema),
  /** Invites not used yet (owners only; empty for staff). */
  invites: z.array(InviteSchema),
  /** The caller can invite, change roles and remove people (an owner). */
  canManage: z.boolean(),
});
export type People = z.infer<typeof PeopleSchema>;

/** An Indian mobile number, as typed (as at sign-in: a +91 or 0 in front is fine), as E.164. */
export const InvitePhoneSchema = z
  .string()
  .trim()
  .max(20)
  .refine((raw) => parseIndianMobile(raw).ok, { error: 'Enter a 10-digit Indian mobile number' })
  .transform((raw) => {
    const parsed = parseIndianMobile(raw);
    return parsed.ok ? `+91${parsed.digits}` : raw;
  });

export const CreateInviteSchema = z.strictObject({
  role: z.enum(BUSINESS_ROLES),
  phone: InvitePhoneSchema,
});
export type CreateInvite = z.input<typeof CreateInviteSchema>;
export const InviteResponseSchema = z.object({ invite: InviteSchema });

export const UpdateMemberSchema = z.strictObject({ role: z.enum(BUSINESS_ROLES) });
export type UpdateMember = z.infer<typeof UpdateMemberSchema>;
export const MemberResponseSchema = z.object({ member: MemberSchema });

/**
 * What an invite link shows the person who opened it. open: theirs to accept; joined: they're in (by this
 * invite or already); expired, used, cancelled: it can't be used; not_for_you: it's for another number
 * (and says nothing else).
 */
export const INVITE_STATES = ['open', 'joined', 'expired', 'used', 'cancelled', 'not_for_you'] as const;
export const InvitePreviewSchema = z.object({
  state: z.enum(INVITE_STATES),
  /** The number it's for, masked. */
  phone: z.string(),
  role: z.enum(BUSINESS_ROLES).nullable(),
  business: z.object({ id: z.string().nullable(), name: z.string(), gstin: z.string() }).nullable(),
  invitedBy: z.string().nullable(),
  expiresAt: z.string().nullable(),
});
export type InvitePreview = z.infer<typeof InvitePreviewSchema>;

export const APPROVAL_STATUSES = ['pending', 'approved', 'sent_back', 'withdrawn'] as const;
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number];
/** changed: the return changed after it was sent; filed: filed another way; removed: the sender left. */
export const APPROVAL_WITHDRAWN_REASONS = ['changed', 'filed', 'removed'] as const;

/** What the owner is shown: the return as it was sent. Amounts in paise. */
export const ApprovalSnapshotSchema = z.discriminatedUnion('form', [
  z.object({
    form: z.literal('gstr1'),
    invoices: z.number().int(),
    taxable: z.number().int(),
    tax: z.number().int(),
    sections: z.array(
      z.object({
        section: z.enum(['b2b', 'b2cl', 'b2cs']),
        count: z.number().int(),
        taxable: z.number().int(),
        tax: z.number().int(),
      }),
    ),
    /** The late fee if it's filed today (as sent), its days and the fee a day. */
    lateFee: z.number().int(),
    daysLate: z.number().int(),
    perDay: z.number().int(),
    /** Sent to be filed NIL (Phase 12). */
    nil: z.boolean().optional(),
  }),
  z.object({
    form: z.literal('gstr3b'),
    /** Tax for the month (output tax and reverse charge, all heads). */
    tax: z.number().int(),
    /** Paid from the credit ledger (ITC). */
    credit: z.number().int(),
    /** Paid in cash: what's already in the cash ledger, and what a challan has to bring. */
    fromLedger: z.number().int(),
    toPay: z.number().int(),
    interest: z.number().int(),
    lateFee: z.number().int(),
    /**
     * Sent to be filed NIL (Phase 12): `lateFee` is then this return's own, at the NIL rate (a later GSTR-3B
     * pays it), for `daysLate` days at `perDay`.
     */
    nil: z.boolean().optional(),
    daysLate: z.number().int().optional(),
    perDay: z.number().int().optional(),
  }),
]);
export type ApprovalSnapshot = z.infer<typeof ApprovalSnapshotSchema>;

const PersonSchema = z.object({ userId: z.string(), name: z.string().nullable() });

export const ApprovalRequestSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  businessName: z.string(),
  returnId: z.string(),
  form: z.enum(['gstr1', 'gstr3b']),
  period: z.string(),
  status: z.enum(APPROVAL_STATUSES),
  withdrawnReason: z.enum(APPROVAL_WITHDRAWN_REASONS).nullable(),
  sentBy: PersonSchema,
  sentAt: z.string(),
  decidedBy: PersonSchema.nullable(),
  decidedAt: z.string().nullable(),
  /** Sent back: the owner's comment. */
  comment: z.string().nullable(),
  remindedAt: z.string().nullable(),
  snapshot: ApprovalSnapshotSchema,
  /** Approved: the filing it started, as it stands. */
  filing: z
    .object({ jobId: z.string(), status: z.string(), arn: z.string().nullable(), filedAt: z.string().nullable() })
    .nullable(),
  /** The owners who can approve it, by name (for "Send to Rahul for approval"). */
  owners: z.array(z.string()),
  /** What the caller can do with it. */
  you: z.object({
    role: z.enum(BUSINESS_ROLES),
    canDecide: z.boolean(),
    canRemind: z.boolean(),
    /** When the sender can remind again (null: now, or never). */
    remindAt: z.string().nullable(),
  }),
  /** Opens the return in the chat (a chat "open" ref, like the bell's links). */
  open: z.string(),
});
export type ApprovalRequest = z.infer<typeof ApprovalRequestSchema>;

export const ApprovalListQuerySchema = z.strictObject({
  /** pending: waiting for an owner; done: decided or withdrawn. Both when not given. */
  status: z.enum(['pending', 'done']).optional(),
});
export const ApprovalListSchema = z.object({
  /** Newest first: requests for businesses the caller owns, and the ones they sent. */
  requests: z.array(ApprovalRequestSchema),
  /** How many wait for the caller's decision (the menu's badge). */
  waiting: z.number().int(),
});
export type ApprovalList = z.infer<typeof ApprovalListSchema>;

export const ApprovalResponseSchema = z.object({ request: ApprovalRequestSchema });
export const ApproveResponseSchema = z.object({
  request: ApprovalRequestSchema,
  /** Where the owner carries on (the chat, at this return: paying, then the EVC code). */
  open: z.string(),
});
export type ApproveResponse = z.infer<typeof ApproveResponseSchema>;

export const SendBackSchema = z.strictObject({
  comment: z.string().trim().min(1, { error: 'Say what to change' }).max(APPROVAL_COMMENT_MAX),
});
export type SendBack = z.infer<typeof SendBackSchema>;

/** A code never travels through the conversation event API in new clients. */
export const CodeResponseSchema = z.object({
  challengeId: z.string(),
  result: z.enum(['accepted', 'wrong', 'expired', 'locked', 'invalid_format', 'failed']),
});
export type CodeResponse = z.infer<typeof CodeResponseSchema>;
